import { useEffect, useState, useMemo, useRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useSearch } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useWorkspace } from "@/hooks/use-workspace";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { ColdEmailHealthCard } from "@/components/admin/cold-email-health-card";
import { ColdEmailPolicyCard } from "@/components/admin/cold-email-policy-card";
import { InfraStorefront } from "@/components/infra-storefront";
import { SendingDomainsTab } from "@/components/sending-domains-tab";
import { COLD_MODE_FRAMING } from "@shared/cold-domain-modes";
import {
  Loader2, RefreshCw, Mail, ShieldCheck, Globe, BellRing, BarChart3,
  CheckCircle2, AlertTriangle, XCircle, Info, HelpCircle, ExternalLink, Plus, Trash2,
  Link as LinkIcon, Unlink, TrendingUp, Inbox, Download, Send, Copy,
  LayoutDashboard, Flame, Snowflake, Settings2, Server, Bell,
} from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { cn } from "@/lib/utils";
import { apiRequest, queryClient, withCsrfHeader } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { EmailAlertSettings, EmailHealthAlert, MassEmailSettings } from "@shared/schema";

interface EmailSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inPanel?: boolean;
}

type CheckStatus = "pass" | "warn" | "fail" | "unknown";

interface OverviewResponse {
  headline: string;
  headlineTone: "ok" | "watch" | "atRisk";
  mailboxes: { total: number; active: number; warming: number; paused: number };
  last7Days: {
    sent: number; hardBounce: number; softBounce: number; optOut: number;
    reply: number; failed: number; bounceRate: number; optOutRate: number; replyRate: number;
  };
  domains: { total: number; checked: number; healthy: number; withIssues: number; worstStatus: CheckStatus };
  alerts: { active: number; atRisk: number; watch: number; recent: EmailHealthAlert[] };
  postmaster: {
    configured: boolean;
    connected: boolean;
    accountEmail: string | null;
    lastSyncedAt: string | null;
    lastSyncError: string | null;
    worstSignal: { severity: "ok" | "watch" | "atRisk" | "unknown"; message: string; domain: string | null; metric: string | null } | null;
  };
  suppression: { totalSuppressed: number };
}

interface SenderReputationResponse {
  mailboxes: Array<{
    mailboxId: string;
    status: string;
    health: "healthy" | "watch" | "atRisk" | "paused";
    reasons: string[];
    warmupStartDate: string;
    dailyCap: number;
    instantlyEnrolled: boolean;
    instantlyEnrolledAt: string | null;
    instantlyHealthScore: number | null;
    instantlyHealthCheckedAt: string | null;
    instantlySent: number | null;
    instantlyLandedInbox: number | null;
    instantlyLandedSpam: number | null;
    instantlyReceived: number | null;
    warmupPaid: boolean;
    last24Hours: { sent: number; bounce: number; bounceRate: number; watchThreshold: number; pauseThreshold: number };
    last7Days: {
      sent: number; hardBounce: number; softBounce: number; bounce: number;
      optOut: number; reply: number; failed: number;
      bounceRate: number; optOutRate: number; replyRate: number;
      watchBounceThreshold: number; pauseBounceThreshold: number; watchOptOutThreshold: number;
    };
  }>;
  thresholds: { watchBounce24h: number; watchBounce7d: number; watchOptOut7d: number; watchSpamRate: number };
}

interface DomainListItem {
  domain: string;
  source: "auto" | "manual";
  dkimSelector: string;
  id: string | null;
  notes: string | null;
}

interface DomainCheckResult {
  domain: string;
  spf: { status: CheckStatus; record: string | null; message: string; recommendation?: string };
  dkim: { status: CheckStatus; selector: string; hostname: string; record: string | null; message: string; recommendation?: string };
  dmarc: { status: CheckStatus; record: string | null; policy: string | null; pct: number | null; rua: string | null; ruf: string | null; message: string; recommendation?: string };
  overall: CheckStatus;
  checkedAt: string;
  source?: "auto" | "manual";
}

interface DmarcAggregateSummaryItem {
  domain: string;
  reportCount: number;
  windowBegin: string | null;
  windowEnd: string | null;
  totalMessages: number;
  dmarcPassCount: number;
  dmarcFailCount: number;
  dmarcPassRate: number;
  dkimPassRate: number;
  spfPassRate: number;
  topSources: Array<{
    sourceIp: string;
    host: string | null;
    count: number;
    dmarcPass: number;
    dmarcFail: number;
    dkimPass: number;
    spfPass: number;
    dispositions: Record<string, number>;
  }>;
  reporters: string[];
  latestReceivedAt: string | null;
}

interface ComplianceResponse {
  massEmail: MassEmailSettings | null;
  suppression: { total: number; byReason: Record<string, number> };
}

interface AlertsResponse {
  settings: EmailAlertSettings;
  recent: EmailHealthAlert[];
  active: EmailHealthAlert[];
}

interface PostmasterResponse {
  configured: boolean;
  connected: boolean;
  accountEmail: string | null;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  cachedDomains: any;
  sendingDomains: string[];
  walkthrough: { steps: string[]; note: string } | null;
}

interface PostmasterIpShares {
  HIGH: number | null;
  MEDIUM: number | null;
  LOW: number | null;
  BAD: number | null;
}
interface PostmasterHistoryPoint {
  date: string;
  domainReputation: string | null;
  spamRate: number | null;
  spfPassRate: number | null;
  dkimPassRate: number | null;
  dmarcPassRate: number | null;
  deliveryErrorRate: number | null;
  ipShares: PostmasterIpShares;
  ipTotal: number | null;
}
interface PostmasterHistorySeries {
  domain: string;
  points: PostmasterHistoryPoint[];
}
type RechartsValue = number | string | Array<number | string>;
interface PostmasterHistoryResponse {
  days: number;
  series: PostmasterHistorySeries[];
  latest: Array<{
    domain: string;
    sampleDate: string;
    domainReputation: string | null;
    spamRate: number | null;
    spfPassRate: number | null;
    dkimPassRate: number | null;
    dmarcPassRate: number | null;
    signal: { severity: "ok" | "watch" | "atRisk" | "unknown"; metric: string | null; message: string };
  }>;
  worstSignal: { severity: "ok" | "watch" | "atRisk" | "unknown"; message: string; domain: string | null; metric: string | null } | null;
}

// Map a domain reputation string to a 0-3 numeric value so it can be plotted.
function reputationToScore(rep: string | null): number | null {
  switch ((rep || "").toUpperCase()) {
    case "HIGH": return 3;
    case "MEDIUM": return 2;
    case "LOW": return 1;
    case "BAD": return 0;
    default: return null;
  }
}
function scoreToReputationLabel(score: number | undefined): string {
  if (score === 3) return "HIGH";
  if (score === 2) return "MEDIUM";
  if (score === 1) return "LOW";
  if (score === 0) return "BAD";
  return "—";
}

function formatPct(value: number, digits: number = 2): string {
  if (!Number.isFinite(value)) return "—";
  return `${(value * 100).toFixed(digits)}%`;
}

function StatusIcon({ status }: { status: CheckStatus }) {
  if (status === "pass") return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  if (status === "warn") return <AlertTriangle className="h-4 w-4 text-amber-600" />;
  if (status === "fail") return <XCircle className="h-4 w-4 text-destructive" />;
  return <Info className="h-4 w-4 text-muted-foreground" />;
}

function HealthBadge({ health }: { health: "healthy" | "watch" | "atRisk" | "paused" }) {
  const map: Record<string, { label: string; cls: string }> = {
    healthy: { label: "Healthy", cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" },
    watch: { label: "Watch", cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" },
    atRisk: { label: "At risk", cls: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200" },
    paused: { label: "Paused", cls: "bg-zinc-200 text-zinc-800 dark:bg-zinc-800 dark:text-zinc-200" },
  };
  const cfg = map[health];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.cls}`} data-testid={`badge-health-${health}`}>{cfg.label}</span>;
}

function HelpTip({ text }: { text: string }) {
  // Click-based popover (instead of hover tooltip) so the "?" works
  // reliably on touch devices and for users who don't hover.
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex align-middle"
          aria-label="More info"
          data-testid="button-help-tip"
          onClick={(e) => e.stopPropagation()}
        >
          <HelpCircle className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="max-w-xs text-xs leading-relaxed" side="top">
        {text}
      </PopoverContent>
    </Popover>
  );
}

// =====================================================================
// Overview tab
// =====================================================================
function OverviewTab() {
  const { data, isLoading, refetch, isRefetching } = useQuery<OverviewResponse>({
    queryKey: ["/api/admin/email-settings/overview"],
  });

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }
  if (!data) return <p className="text-sm text-muted-foreground">No data available.</p>;

  const headlineCls = data.headlineTone === "atRisk"
    ? "border-destructive/50 bg-destructive/5"
    : data.headlineTone === "watch"
      ? "border-amber-500/50 bg-amber-500/5"
      : "border-emerald-500/50 bg-emerald-500/5";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Email Health Overview</h2>
        <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isRefetching} data-testid="button-refresh-overview">
          {isRefetching ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Refresh
        </Button>
      </div>

      <Alert className={headlineCls} data-testid="alert-overview-headline">
        <AlertTitle className="flex items-center gap-2">
          {data.headlineTone === "ok" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> :
            data.headlineTone === "watch" ? <AlertTriangle className="h-4 w-4 text-amber-600" /> :
              <XCircle className="h-4 w-4 text-destructive" />}
          {data.headline}
        </AlertTitle>
        <AlertDescription className="text-xs mt-1">
          {data.alerts.active} active alert{data.alerts.active === 1 ? "" : "s"} · {data.mailboxes.total} mailbox{data.mailboxes.total === 1 ? "" : "es"} ·
          {" "}{data.domains.total} sending domain{data.domains.total === 1 ? "" : "s"}
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card data-testid="card-overview-volume">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Last 7 days <HelpTip text="A rolling 7-day picture of the workspace's email program." /></CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Emails sent</span><span data-testid="text-overview-sent">{data.last7Days.sent.toLocaleString()}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Bounce rate <HelpTip text="Hard + soft bounces over the 7-day window. Anything over 4% is bad news." /></span><span data-testid="text-overview-bounce">{formatPct(data.last7Days.bounceRate)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Reply rate</span><span>{formatPct(data.last7Days.replyRate)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Opt-out rate</span><span>{formatPct(data.last7Days.optOutRate)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Failed sends</span><span>{data.last7Days.failed.toLocaleString()}</span></div>
          </CardContent>
        </Card>

        <Card data-testid="card-overview-mailboxes">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Mail className="h-4 w-4" /> Mailboxes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span>{data.mailboxes.total}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Active</span><span className="text-emerald-700 dark:text-emerald-300">{data.mailboxes.active}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Warming</span><span>{data.mailboxes.warming}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Paused</span><span className={data.mailboxes.paused > 0 ? "text-destructive" : ""}>{data.mailboxes.paused}</span></div>
          </CardContent>
        </Card>

        <Card data-testid="card-overview-domains">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Globe className="h-4 w-4" /> Sending domains</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span>{data.domains.total}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Healthy</span><span className="text-emerald-700 dark:text-emerald-300">{data.domains.healthy}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">With issues</span><span className={data.domains.withIssues > 0 ? "text-amber-700 dark:text-amber-300" : ""}>{data.domains.withIssues}</span></div>
          </CardContent>
        </Card>

        <Card data-testid="card-overview-postmaster">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Google Postmaster</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {!data.postmaster.configured && (
              <p className="text-muted-foreground text-xs">Server is missing Google OAuth credentials.</p>
            )}
            {data.postmaster.configured && (
              <>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  {data.postmaster.connected
                    ? <Badge variant="default" className="text-xs">Connected</Badge>
                    : <Badge variant="outline" className="text-xs">Not connected</Badge>}
                </div>
                {data.postmaster.accountEmail && (
                  <div className="flex justify-between text-xs"><span className="text-muted-foreground">Account</span><span className="truncate ml-2 max-w-[180px]">{data.postmaster.accountEmail}</span></div>
                )}
                {data.postmaster.connected && data.postmaster.worstSignal && (
                  <div className="rounded border p-2 mt-1 text-xs space-y-0.5" data-testid={`postmaster-worst-signal-${data.postmaster.worstSignal.severity}`}>
                    <div className="flex items-center gap-1.5">
                      {data.postmaster.worstSignal.severity === "atRisk" && <XCircle className="h-3.5 w-3.5 text-destructive" />}
                      {data.postmaster.worstSignal.severity === "watch" && <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />}
                      {data.postmaster.worstSignal.severity === "ok" && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
                      {data.postmaster.worstSignal.severity === "unknown" && <Info className="h-3.5 w-3.5 text-muted-foreground" />}
                      <span className="font-medium">Worst current signal</span>
                    </div>
                    <p className="text-muted-foreground" data-testid="text-postmaster-worst-signal-message">{data.postmaster.worstSignal.message}</p>
                  </div>
                )}
                {data.postmaster.connected && !data.postmaster.worstSignal && (
                  <p className="text-xs text-muted-foreground">No reputation samples yet — first sync will appear within ~24 hours.</p>
                )}
                {data.postmaster.lastSyncError && (
                  <p className="text-xs text-destructive">Last sync error: {data.postmaster.lastSyncError}</p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Active alerts now live in the Alerts panel below on the Dashboard,
          so the duplicate "recent alerts" card was removed here (Task #392). */}
    </div>
  );
}

// =====================================================================
// Domain Health Scorecard (Task #233)
// =====================================================================
type SignalStatus = "ok" | "watch" | "atRisk" | "unknown";
type DomainVerdict = "healthy" | "watch" | "atRisk" | "noData";

interface DomainHealthSignal {
  key: string;
  label: string;
  status: SignalStatus;
  value: string;
  explanation?: string;
}
interface DomainHealthScorecard {
  domain: string;
  verdict: DomainVerdict;
  hasPostmasterData: boolean;
  mailboxCount: number;
  sampleDate: string | null;
  signals: DomainHealthSignal[];
}
interface DomainHealthResponse {
  domains: DomainHealthScorecard[];
  postmasterConnected: boolean;
}

function VerdictBadge({ verdict }: { verdict: DomainVerdict }) {
  const map: Record<DomainVerdict, { label: string; cls: string }> = {
    healthy: { label: "Healthy", cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" },
    watch: { label: "Watch", cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" },
    atRisk: { label: "At risk", cls: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200" },
    noData: { label: "Not enough data", cls: "bg-zinc-200 text-zinc-800 dark:bg-zinc-800 dark:text-zinc-200" },
  };
  const cfg = map[verdict];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.cls}`} data-testid={`badge-verdict-${verdict}`}>{cfg.label}</span>;
}

function SignalStatusIcon({ status }: { status: SignalStatus }) {
  if (status === "ok") return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  if (status === "watch") return <AlertTriangle className="h-4 w-4 text-amber-600" />;
  if (status === "atRisk") return <XCircle className="h-4 w-4 text-destructive" />;
  return <Info className="h-4 w-4 text-muted-foreground" />;
}

function DomainHealthScorecardTab() {
  const { data, isLoading, refetch, isRefetching } = useQuery<DomainHealthResponse>({
    queryKey: ["/api/admin/email-settings/domain-health"],
  });
  const [guideOpen, setGuideOpen] = useState(false);
  const [guideDomain, setGuideDomain] = useState<{ domain: string; dkimSelector: string } | null>(null);

  const openGuide = (domain: string) => {
    setGuideDomain({ domain, dkimSelector: "google" });
    setGuideOpen(true);
  };

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }
  if (!data) return <p className="text-sm text-muted-foreground">No data available.</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Domain Health Scorecard</h2>
          <p className="text-sm text-muted-foreground">One verdict per sending domain — fusing Postmaster reputation, spam reports, authentication, bounces, opt-outs and warm-up into a single read.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isRefetching} data-testid="button-refresh-domain-health">
          {isRefetching ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Refresh
        </Button>
      </div>

      {data.domains.length === 0 && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>No sending domains yet</AlertTitle>
          <AlertDescription>Connect a mailbox or add a domain under the Domains tab to start tracking domain health.</AlertDescription>
        </Alert>
      )}

      <div className="space-y-3">
        {data.domains.map((d) => (
          <Card key={d.domain} data-testid={`card-domain-health-${d.domain}`}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <CardTitle className="text-sm font-medium" data-testid={`text-domain-${d.domain}`}>{d.domain}</CardTitle>
                  <CardDescription className="text-xs">
                    {d.mailboxCount} mailbox{d.mailboxCount === 1 ? "" : "es"}
                    {d.sampleDate ? ` · Postmaster as of ${d.sampleDate}` : ""}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => openGuide(d.domain)}
                    data-testid={`button-setup-guide-health-${d.domain}`}
                  >
                    <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                    Set up
                  </Button>
                  <VerdictBadge verdict={d.verdict} />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {d.verdict === "noData" && !d.hasPostmasterData && (
                <Alert data-testid={`alert-domain-nodata-${d.domain}`}>
                  <Info className="h-4 w-4" />
                  <AlertTitle className="text-sm">Not enough data yet</AlertTitle>
                  <AlertDescription className="text-xs">
                    No Google Postmaster data for this domain. Click <strong>Set up</strong> above to walk through DNS authentication and connecting Postmaster Tools.
                  </AlertDescription>
                </Alert>
              )}
              <div className="divide-y rounded-md border">
                {d.signals.map((sig) => (
                  <div key={sig.key} className="p-2 text-xs" data-testid={`signal-${d.domain}-${sig.key}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-1.5"><SignalStatusIcon status={sig.status} /> {sig.label}</span>
                      <span className="font-medium" data-testid={`signal-value-${d.domain}-${sig.key}`}>{sig.value}</span>
                    </div>
                    {sig.explanation && (
                      <p
                        className={`mt-1 ml-5 ${sig.status === "atRisk" ? "text-red-700 dark:text-red-300" : "text-amber-700 dark:text-amber-300"}`}
                        data-testid={`signal-explanation-${d.domain}-${sig.key}`}
                      >
                        {sig.explanation}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {guideDomain && (
        <EmailAuthSetupGuide
          domain={guideDomain.domain}
          dkimSelector={guideDomain.dkimSelector}
          open={guideOpen}
          onOpenChange={setGuideOpen}
        />
      )}
    </div>
  );
}

function WarmupScoreBar({ score }: { score: number | null }) {
  if (score === null) return <span className="text-xs text-muted-foreground">Pending first poll…</span>;
  const color = score >= 70 ? "bg-green-500" : score >= 40 ? "bg-amber-500" : "bg-red-500";
  const label = score >= 70 ? "Healthy" : score >= 40 ? "Building" : "Low";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${Math.min(100, score)}%` }} />
      </div>
      <span className="text-xs font-medium tabular-nums w-8 text-right">{score}%</span>
      <span className={`text-xs ${score >= 70 ? "text-green-600 dark:text-green-400" : score >= 40 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400"}`}>{label}</span>
    </div>
  );
}

// ── Warmup info / journey modal ──────────────────────────────────────────────
// Shown first when the user clicks "Boost Inbox Placement". Explains the
// warmup journey timeline so they know what they're activating before paying.
const WARMUP_PHASES = [
  {
    range: "Days 1–14",
    title: "Calibration",
    icon: "🌱",
    body: "Warmup begins at low volume (5–10 emails/day). The Instantly.ai network — 200k+ active inboxes — receives your emails, opens them, and replies. Gmail registers the first positive trust signals from your address.",
  },
  {
    range: "Days 15–30",
    title: "Ramp-Up",
    icon: "📈",
    body: "Daily volume increases automatically. Emails are consistently opened and moved from spam to inbox by real network members. Gmail's sending history for your mailbox begins to solidify.",
  },
  {
    range: "Days 31–60",
    title: "Reputation Building",
    icon: "🏗️",
    body: "Positive open, reply, and inbox-placement signals accumulate. Your domain's spam-complaint rate drops. Most mailboxes enter 'Building' health status — inbox placement starts improving measurably for real sends.",
  },
  {
    range: "Days 61–120",
    title: "Approaching Healthy",
    icon: "🔥",
    body: "Consistent engagement tips the scales. Gmail increasingly routes your real campaign and outreach emails to the primary inbox. Health score climbs toward the 70+ 'Healthy' threshold (score ≥ 70).",
  },
  {
    range: "Day 120+",
    title: "Maintained",
    icon: "✅",
    body: "Most mailboxes reach Healthy and stay there. Keep warmup running permanently — sender reputation decays without ongoing positive signals. Think of it as a maintenance programme, not a one-time fix.",
  },
] as const;

function WarmupInfoModal({
  mailboxId,
  open,
  onOpenChange,
  onActivate,
}: {
  mailboxId: string | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onActivate: () => void;
}) {
  if (!open || !mailboxId) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => onOpenChange(false)}>
      <div
        className="bg-background border rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 pb-3 border-b flex-shrink-0">
          <div className="flex items-center gap-2 mb-1">
            <Flame className="h-5 w-5 text-orange-500" />
            <h3 className="text-base font-semibold">Boost Inbox Placement</h3>
          </div>
          <p className="text-xs text-muted-foreground">
            Here's exactly what happens to <span className="font-mono font-medium text-foreground">{mailboxId}</span> over the warmup journey.
          </p>
        </div>

        {/* Timeline */}
        <div className="overflow-y-auto flex-1 p-6 space-y-0">
          {WARMUP_PHASES.map((phase, i) => (
            <div key={i} className="flex gap-3">
              {/* Spine */}
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800 flex items-center justify-center text-sm flex-shrink-0">
                  {phase.icon}
                </div>
                {i < WARMUP_PHASES.length - 1 && (
                  <div className="w-px flex-1 bg-border mt-1 mb-1 min-h-[24px]" />
                )}
              </div>
              {/* Content */}
              <div className={`pb-5 ${i === WARMUP_PHASES.length - 1 ? "" : ""}`}>
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide leading-none mb-0.5">{phase.range}</p>
                <p className="text-sm font-semibold leading-tight mb-1">{phase.title}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{phase.body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-6 pt-3 border-t flex-shrink-0 space-y-3">
          <p className="text-[11px] text-muted-foreground">
            $10 one-time activation fee, then $1.99/month as long as warmup is running. Cancel any time — your activation fee is yours to keep and re-enroll is free.
          </p>
          <div className="flex gap-2">
            <Button
              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
              onClick={() => { onOpenChange(false); onActivate(); }}
              data-testid="button-warmup-info-activate"
            >
              <Flame className="h-4 w-4 mr-1.5" />
              Activate · $10 + $1.99/mo
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-warmup-info-cancel">
              Not now
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Warmup payment modal ─────────────────────────────────────────────────────
// Inner form that lives inside <Elements> so it can call useStripe/useElements
function WarmupPaymentForm({
  clientSecret,
  subscriptionId,
  firstCharge,
  isFirstActivation,
  onSubscribed,
  onCancel,
}: {
  clientSecret: string;
  subscriptionId: string;
  firstCharge: number;
  isFirstActivation: boolean;
  onSubscribed: (subscriptionId: string) => void;
  onCancel: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const { toast } = useToast();

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setPaying(true);
    setCardError(null);
    const card = elements.getElement(CardElement);
    if (!card) { setPaying(false); return; }
    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: { card },
    });
    setPaying(false);
    if (error) {
      setCardError(error.message || "Payment failed");
    } else if (paymentIntent?.status === "succeeded") {
      toast({ title: "Payment confirmed", description: isFirstActivation ? "Warmup activated — entering your app password…" : "Resuming warmup…" });
      onSubscribed(subscriptionId);
    }
  };

  const dollarAmount = (firstCharge / 100).toFixed(2);
  const label = isFirstActivation ? `Pay $${dollarAmount} — Activate Warmup` : `Pay $${dollarAmount} — Resume Warmup`;

  return (
    <div className="space-y-4">
      <div className="border rounded-md p-3 bg-muted/40">
        <CardElement
          options={{
            style: {
              base: { fontSize: "14px", color: "inherit", "::placeholder": { color: "#9ca3af" } },
              invalid: { color: "#ef4444" },
            },
          }}
          onChange={(e) => setCardError(e.error?.message || null)}
        />
      </div>
      {cardError && <p className="text-xs text-destructive">{cardError}</p>}
      <div className="flex gap-2">
        <Button
          className="flex-1"
          onClick={handlePay}
          disabled={paying || !stripe}
          data-testid="button-pay-warmup"
        >
          {paying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Flame className="h-4 w-4 mr-1 text-orange-500" />}
          {label}
        </Button>
        <Button variant="outline" onClick={onCancel} data-testid="button-cancel-pay-warmup">Cancel</Button>
      </div>
    </div>
  );
}

function WarmupPaymentModal({
  mailboxId,
  open,
  onOpenChange,
  onSubscribed,
}: {
  mailboxId: string | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSubscribed: (subscriptionId: string) => void;
}) {
  const { toast } = useToast();
  const [sub, setSub] = useState<{
    clientSecret: string;
    publishableKey: string;
    subscriptionId: string;
    firstCharge: number;
    isFirstActivation: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const stripePromise = useMemo(
    () => (sub?.publishableKey ? loadStripe(sub.publishableKey) : null),
    [sub?.publishableKey],
  );

  useEffect(() => {
    if (!open || !mailboxId) { setSub(null); return; }
    setLoading(true);
    apiRequest("POST", "/api/admin/email-settings/mailbox-warmup/create-subscription", { mailboxId })
      .then((r: any) => {
        setSub({
          clientSecret: r.clientSecret,
          publishableKey: r.publishableKey,
          subscriptionId: r.subscriptionId,
          firstCharge: r.firstCharge,
          isFirstActivation: !!r.isFirstActivation,
        });
      })
      .catch((e: any) => {
        toast({ title: "Error", description: e?.message || "Could not initialize payment", variant: "destructive" });
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
  }, [open, mailboxId]);

  if (!open) return null;

  const title = sub?.isFirstActivation
    ? "Activate Inbox Warmup — $10 + $1.99/mo"
    : "Resume Inbox Warmup — $1.99/mo";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => onOpenChange(false)}>
      <div className="bg-background border rounded-xl shadow-xl w-full max-w-md mx-4 p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <Flame className="h-5 w-5 text-orange-500" />
          <h3 className="text-base font-semibold">{title}</h3>
        </div>
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription className="text-xs">
            {sub?.isFirstActivation ? (
              <>
                <strong>Today: ${((sub.firstCharge) / 100).toFixed(2)}</strong> ($10 one-time activation + first month $1.99).
                Then <strong>$1.99/month</strong> as long as warmup is running — cancel any time to stop billing.
                Your activation fee is permanent: future re-enrollments are free.
              </>
            ) : (
              <>
                <strong>$1.99/month</strong> starting today. Your $10 activation is already on file — no setup fee.
                Cancel any time.
              </>
            )}
            {" "}Warmup emails (up to 80/day) continuously improve your Gmail sender reputation through Instantly.ai's 200k+ inbox network.
          </AlertDescription>
        </Alert>
        <div className="space-y-1">
          <Label className="text-xs font-medium">Mailbox</Label>
          <p className="text-sm font-mono bg-muted px-2 py-1 rounded">{mailboxId}</p>
        </div>
        {loading || !sub ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Elements stripe={stripePromise} options={{ clientSecret: sub.clientSecret }}>
            <WarmupPaymentForm
              clientSecret={sub.clientSecret}
              subscriptionId={sub.subscriptionId}
              firstCharge={sub.firstCharge}
              isFirstActivation={sub.isFirstActivation}
              onSubscribed={onSubscribed}
              onCancel={() => onOpenChange(false)}
            />
          </Elements>
        )}
      </div>
    </div>
  );
}

function WarmupEnrollDialog({
  mailboxId,
  open,
  onOpenChange,
  onSuccess,
  subscriptionId,
  mailboxType = "gmail",
}: {
  mailboxId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSuccess: () => void;
  subscriptionId?: string | null;
  mailboxType?: "gmail" | "byod_cold";
}) {
  const { toast } = useToast();
  const isByodCold = mailboxType === "byod_cold";
  const [appPassword, setAppPassword] = useState("");
  const [smtpHost, setSmtpHost] = useState("smtp.gmail.com");
  const [imapHost, setImapHost] = useState("imap.gmail.com");
  const [showAdvanced, setShowAdvanced] = useState(false);

  const enroll = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/admin/email-settings/mailbox-warmup/enroll", {
        mailboxId,
        ...(!isByodCold ? { appPassword: appPassword.trim(), smtpHost, smtpPort: 587, imapHost, imapPort: 993 } : {}),
        ...(subscriptionId ? { subscriptionId } : {}),
      }),
    onSuccess: () => {
      toast({ title: "Inbox warmup enabled", description: `${mailboxId} is now enrolled. Health scores update hourly.` });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-settings/sender-reputation"] });
      onSuccess();
      onOpenChange(false);
      setAppPassword("");
    },
    onError: (e: any) => {
      toast({ title: "Enrollment failed", description: e?.message || "Could not enroll mailbox", variant: "destructive" });
    },
  });

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center bg-black/50 ${open ? "" : "hidden"}`} onClick={() => onOpenChange(false)}>
      <div className="bg-background border rounded-xl shadow-xl w-full max-w-md mx-4 p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <Flame className="h-5 w-5 text-orange-500" />
          <h3 className="text-base font-semibold">Enable Inbox Warmup</h3>
        </div>

        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription className="text-xs">
            {isByodCold
              ? "Instantly.ai will send ~10–20 small emails daily from this mailbox within a trusted network of 200k+ inboxes. Credentials already stored from your BYOD cold-infrastructure setup — no app password needed."
              : <>Instantly.ai will send ~10–20 small emails daily from this mailbox within a trusted network of 200k+ inboxes.
                Those inboxes open and reply, which continuously improves your Gmail sender reputation.{" "}
                <strong>Your app password is passed directly to Instantly and is never stored by Clarik.</strong></>}
          </AlertDescription>
        </Alert>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs font-medium">Mailbox</Label>
            <p className="text-sm font-mono bg-muted px-2 py-1 rounded">{mailboxId}</p>
          </div>

          {!isByodCold && (
            <>
              <div className="space-y-1">
                <Label htmlFor="app-password" className="text-xs font-medium">
                  Google App Password{" "}
                  <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer"
                    className="text-primary underline underline-offset-2 ml-1" data-testid="link-app-password-help">
                    Generate one <ExternalLink className="h-3 w-3 inline" />
                  </a>
                </Label>
                <Input
                  id="app-password"
                  type="password"
                  placeholder="xxxx xxxx xxxx xxxx"
                  value={appPassword}
                  onChange={(e) => setAppPassword(e.target.value)}
                  data-testid="input-app-password"
                  className="font-mono text-sm"
                />
                <p className="text-[11px] text-muted-foreground">Requires 2-Step Verification on the Google account. Create the password under "Security → App passwords".</p>
              </div>

              <button
                type="button"
                className="text-xs text-muted-foreground underline underline-offset-2"
                onClick={() => setShowAdvanced(!showAdvanced)}
                data-testid="button-toggle-advanced-smtp"
              >
                {showAdvanced ? "Hide" : "Show"} advanced SMTP settings
              </button>

              {showAdvanced && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">SMTP Host</Label>
                    <Input value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} className="text-xs h-8" data-testid="input-smtp-host" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">IMAP Host</Label>
                    <Input value={imapHost} onChange={(e) => setImapHost(e.target.value)} className="text-xs h-8" data-testid="input-imap-host" />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex gap-2 pt-1">
          <Button
            className="flex-1"
            onClick={() => enroll.mutate()}
            disabled={(!isByodCold && !appPassword.trim()) || enroll.isPending}
            data-testid="button-confirm-enroll-warmup"
          >
            {enroll.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Flame className="h-4 w-4 mr-1" />}
            Enable Warmup
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-cancel-enroll-warmup">Cancel</Button>
        </div>
      </div>
    </div>
  );
}

function SenderReputationTab() {
  const { data, isLoading, refetch, isRefetching } = useQuery<SenderReputationResponse>({
    queryKey: ["/api/admin/email-settings/sender-reputation"],
  });
  const { toast } = useToast();
  const [enrollingMailbox, setEnrollingMailbox] = useState<string | null>(null);
  const [enrollingMailboxType, setEnrollingMailboxType] = useState<"gmail" | "byod_cold">("gmail");
  const [infoMailbox, setInfoMailbox] = useState<string | null>(null);
  const [payingMailbox, setPayingMailbox] = useState<string | null>(null);
  const [payingMailboxType, setPayingMailboxType] = useState<"gmail" | "byod_cold">("gmail");
  const [pendingSubscriptionId, setPendingSubscriptionId] = useState<string | null>(null);

  const unenroll = useMutation({
    mutationFn: (mailboxId: string) =>
      apiRequest("DELETE", `/api/admin/email-settings/mailbox-warmup/enroll/${encodeURIComponent(mailboxId)}`),
    onSuccess: (_data, mailboxId) => {
      toast({ title: "Warmup disabled", description: `${mailboxId} removed from Instantly warmup.` });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-settings/sender-reputation"] });
    },
    onError: (e: any) => {
      toast({ title: "Failed to disable warmup", description: e?.message || "Unknown error", variant: "destructive" });
    },
  });

  if (isLoading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }
  if (!data) return <p className="text-sm text-muted-foreground">No data available.</p>;

  return (
    <div className="space-y-4">
      {/* Info modal — first step for not-yet-paid mailboxes */}
      <WarmupInfoModal
        mailboxId={infoMailbox}
        open={!!infoMailbox}
        onOpenChange={(v) => { if (!v) setInfoMailbox(null); }}
        onActivate={() => { const mb = infoMailbox; setInfoMailbox(null); if (mb) setPayingMailbox(mb); }}
      />

      {/* Payment modal — shown when a not-yet-paid mailbox clicks Enable */}
      <WarmupPaymentModal
        mailboxId={payingMailbox}
        open={!!payingMailbox}
        onOpenChange={(v) => { if (!v) setPayingMailbox(null); }}
        onSubscribed={(subId) => {
          const mb = payingMailbox;
          const mbType = payingMailboxType;
          setPendingSubscriptionId(subId);
          setPayingMailbox(null);
          if (mb) { setEnrollingMailboxType(mbType); setEnrollingMailbox(mb); }
        }}
      />

      {enrollingMailbox && (
        <WarmupEnrollDialog
          mailboxId={enrollingMailbox}
          open={!!enrollingMailbox}
          onOpenChange={(v) => { if (!v) { setEnrollingMailbox(null); setPendingSubscriptionId(null); } }}
          onSuccess={() => { setEnrollingMailbox(null); setPendingSubscriptionId(null); }}
          subscriptionId={pendingSubscriptionId}
          mailboxType={enrollingMailboxType}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Sender Reputation</h2>
          <p className="text-sm text-muted-foreground">How each connected mailbox is performing — and its Instantly.ai inbox warmup health.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isRefetching} data-testid="button-refresh-reputation">
          {isRefetching ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Refresh
        </Button>
      </div>

      {data.mailboxes.length === 0 && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>No mailboxes connected yet</AlertTitle>
          <AlertDescription>Connect a Gmail account from Integrations to start tracking sender reputation.</AlertDescription>
        </Alert>
      )}

      <div className="space-y-3">
        {data.mailboxes.map((mb) => (
          <Card key={mb.mailboxId} data-testid={`card-reputation-${mb.mailboxId}`}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <CardTitle className="text-sm font-medium" data-testid={`text-mailbox-${mb.mailboxId}`}>{mb.mailboxId}</CardTitle>
                  <CardDescription className="text-xs">Daily send cap: {mb.dailyCap} · Sending since {new Date(mb.warmupStartDate).toLocaleDateString()}</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {mb.instantlyEnrolled && (
                    <Badge variant="secondary" className="text-[10px] flex items-center gap-1 text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-950 border-orange-200 dark:border-orange-800" data-testid={`badge-warmup-${mb.mailboxId}`}>
                      <Flame className="h-3 w-3" /> Warming
                    </Badge>
                  )}
                  <HealthBadge health={mb.health} />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {mb.reasons.length > 0 && (
                <div className="text-xs space-y-1">
                  {mb.reasons.map((r, i) => (
                    <p key={i} className="text-amber-700 dark:text-amber-300 flex items-start gap-1"><AlertTriangle className="h-3 w-3 mt-0.5" /> {r}</p>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div className="border rounded-md p-2">
                  <p className="font-medium mb-1 flex items-center gap-1">Last 24 hours <HelpTip text={`Auto-pauses if bounce rate hits ${formatPct(mb.last24Hours.pauseThreshold)} (watch alert at ${formatPct(mb.last24Hours.watchThreshold)}).`} /></p>
                  <div className="flex justify-between"><span className="text-muted-foreground">Sent</span><span>{mb.last24Hours.sent}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Bounces</span><span>{mb.last24Hours.bounce}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Bounce rate</span><span>{formatPct(mb.last24Hours.bounceRate)}</span></div>
                </div>
                <div className="border rounded-md p-2">
                  <p className="font-medium mb-1 flex items-center gap-1">Last 7 days <HelpTip text={`Auto-pauses if bounce rate hits ${formatPct(mb.last7Days.pauseBounceThreshold)} (watch alert at ${formatPct(mb.last7Days.watchBounceThreshold)}).`} /></p>
                  <div className="flex justify-between"><span className="text-muted-foreground">Sent</span><span>{mb.last7Days.sent}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Bounces</span><span>{mb.last7Days.bounce}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Bounce rate</span><span>{formatPct(mb.last7Days.bounceRate)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Reply rate</span><span>{formatPct(mb.last7Days.replyRate)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Opt-out rate</span><span>{formatPct(mb.last7Days.optOutRate)}</span></div>
                </div>
              </div>

              {/* ── Instantly Warmup section ── */}
              <div className="border rounded-md p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium flex items-center gap-1">
                    <Flame className="h-3.5 w-3.5 text-orange-500" />
                    Inbox Warmup
                    <HelpTip text="Instantly.ai sends warmup emails from this mailbox (up to 80/day, ramped automatically) within a 200k+ inbox network. They get opened and replied to, continuously improving Gmail's trust in this sender. Works best when left running permanently." />
                  </p>
                  <div className="flex items-center gap-1.5">
                    {mb.warmupPaid && !mb.instantlyEnrolled && (
                      <span className="text-[10px] text-green-600 dark:text-green-400 font-medium">✓ Paid</span>
                    )}
                    {mb.instantlyEnrolled ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 text-xs text-muted-foreground hover:text-destructive"
                        onClick={() => {
                          if (confirm(`Disable inbox warmup for ${mb.mailboxId}?`)) unenroll.mutate(mb.mailboxId);
                        }}
                        disabled={unenroll.isPending}
                        data-testid={`button-unenroll-warmup-${mb.mailboxId}`}
                      >
                        {unenroll.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Unlink className="h-3 w-3 mr-1" />}
                        Disable
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 text-xs"
                        onClick={() => {
                          const mbType = (mb as any).mailboxType === "byod_cold" ? "byod_cold" : "gmail";
                          if (mb.warmupPaid) {
                            setEnrollingMailboxType(mbType);
                            setEnrollingMailbox(mb.mailboxId);
                          } else {
                            setPayingMailboxType(mbType);
                            setInfoMailbox(mb.mailboxId);
                          }
                        }}
                        data-testid={`button-enroll-warmup-${mb.mailboxId}`}
                      >
                        <Flame className="h-3 w-3 mr-1 text-orange-500" />
                        {mb.warmupPaid ? "Enable Warmup · $1.99/mo" : "Boost Inbox Placement · $10 + $1.99/mo"}
                      </Button>
                    )}
                  </div>
                </div>

                {mb.instantlyEnrolled ? (
                  <div className="space-y-1.5">
                    <WarmupScoreBar score={mb.instantlyHealthScore} />
                    {mb.instantlySent != null && (
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                        <span>Sent <span className="font-medium text-foreground">{mb.instantlySent.toLocaleString()}</span></span>
                        {mb.instantlySent > 0 && mb.instantlyLandedInbox != null && (
                          <span>📥 Inbox <span className="font-medium text-green-600 dark:text-green-400">{Math.round((mb.instantlyLandedInbox / mb.instantlySent) * 100)}%</span></span>
                        )}
                        {mb.instantlySent > 0 && mb.instantlyLandedSpam != null && (
                          <span>🚫 Spam <span className={`font-medium ${mb.instantlyLandedSpam / mb.instantlySent > 0.1 ? "text-red-600 dark:text-red-400" : "text-muted-foreground"}`}>{Math.round((mb.instantlyLandedSpam / mb.instantlySent) * 100)}%</span></span>
                        )}
                        {mb.instantlyReceived != null && (
                          <span>Received <span className="font-medium text-foreground">{mb.instantlyReceived.toLocaleString()}</span></span>
                        )}
                      </div>
                    )}
                    <div className="flex gap-3 text-[11px] text-muted-foreground">
                      {mb.instantlyEnrolledAt && (
                        <span>Enrolled {new Date(mb.instantlyEnrolledAt).toLocaleDateString()}</span>
                      )}
                      {mb.instantlyHealthCheckedAt && (
                        <span>· Last checked {new Date(mb.instantlyHealthCheckedAt).toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-[11px] text-muted-foreground">
                    Not enrolled. Enable warmup to continuously improve this mailbox's inbox placement rate.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// =====================================================================
// DMARC aggregate (rua) summary subsection — rendered inside each domain
// card so admins see real-world pass/fail rates next to the synthetic DNS
// check. If no reports have been ingested for the domain yet, shows a brief
// upload affordance instead.
// =====================================================================
function DmarcAggregateSection({
  domain,
  summary,
  onUpload,
  isUploading,
}: {
  domain: string;
  summary: DmarcAggregateSummaryItem | null;
  onUpload: (file: File) => void;
  isUploading: boolean;
}) {
  return (
    <CardContent className="border-t pt-3 space-y-2 text-xs" data-testid={`section-dmarc-rua-${domain}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">DMARC reports</span>
          {summary ? (
            <span className="text-muted-foreground">
              {summary.reportCount} report{summary.reportCount === 1 ? "" : "s"} from {summary.reporters.length} reporter{summary.reporters.length === 1 ? "" : "s"}
            </span>
          ) : (
            <span className="text-muted-foreground">No reports ingested yet</span>
          )}
        </div>
        <label className="inline-flex items-center gap-1 text-[11px] text-primary cursor-pointer hover:underline" data-testid={`label-upload-dmarc-${domain}`}>
          <input
            type="file"
            accept=".xml,application/xml,text/xml,.gz,application/gzip"
            className="hidden"
            disabled={isUploading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
              e.target.value = "";
            }}
            data-testid={`input-upload-dmarc-${domain}`}
          />
          {isUploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
          Upload report (.xml or .xml.gz)
        </label>
      </div>

      {summary && summary.reportCount > 0 && (
        <>
          <div className="grid grid-cols-3 gap-2">
            <DmarcStat label="DMARC pass" value={summary.dmarcPassRate} good />
            <DmarcStat label="DKIM pass" value={summary.dkimPassRate} good />
            <DmarcStat label="SPF pass" value={summary.spfPassRate} good />
          </div>
          <p className="text-[10px] text-muted-foreground" data-testid={`text-dmarc-window-${domain}`}>
            {summary.totalMessages.toLocaleString()} messages reported
            {summary.windowBegin && summary.windowEnd && (
              <> · window {new Date(summary.windowBegin).toLocaleDateString()} – {new Date(summary.windowEnd).toLocaleDateString()}</>
            )}
            {summary.latestReceivedAt && (
              <> · last received {new Date(summary.latestReceivedAt).toLocaleString()}</>
            )}
          </p>
          {summary.topSources.length > 0 && (
            <div className="border rounded-md p-2 space-y-1" data-testid={`table-top-sources-${domain}`}>
              <p className="font-medium text-[11px]">Top sending sources</p>
              <table className="w-full text-[11px]">
                <thead className="text-muted-foreground">
                  <tr className="text-left">
                    <th className="pr-2 font-normal">Source IP</th>
                    <th className="pr-2 font-normal">Volume</th>
                    <th className="pr-2 font-normal">DMARC pass</th>
                    <th className="font-normal">Disposition</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.topSources.map((s) => {
                    const passRate = s.count > 0 ? s.dmarcPass / s.count : 0;
                    const dispositions = Object.entries(s.dispositions)
                      .map(([k, v]) => `${k} (${v})`)
                      .join(", ");
                    return (
                      <tr key={s.sourceIp} data-testid={`row-source-${domain}-${s.sourceIp}`}>
                        <td className="pr-2 font-mono text-[10px]">{s.sourceIp}</td>
                        <td className="pr-2">{s.count.toLocaleString()}</td>
                        <td className={`pr-2 ${passRate >= 0.95 ? "text-green-700 dark:text-green-300" : passRate >= 0.5 ? "text-amber-700 dark:text-amber-300" : "text-red-700 dark:text-red-300"}`}>
                          {formatPct(passRate, 0)}
                        </td>
                        <td>{dispositions || "none"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {!summary && (
        <p className="text-[11px] text-muted-foreground">
          Point your DMARC <code>rua=</code> address at a mailbox you control, then upload the XML reports here (gzip is fine). We'll show real-world authentication pass/fail rates per sending source.
        </p>
      )}
    </CardContent>
  );
}

function DmarcStat({ label, value, good }: { label: string; value: number; good?: boolean }) {
  const tone = value >= 0.95 ? "text-green-700 dark:text-green-300"
    : value >= 0.5 ? "text-amber-700 dark:text-amber-300"
    : "text-red-700 dark:text-red-300";
  return (
    <div className="border rounded-md p-2 text-center" data-testid={`stat-dmarc-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className={`text-sm font-semibold ${good ? tone : ""}`}>{formatPct(value, 1)}</div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}

// =====================================================================
// Email Authentication Setup Guide — 4-step wizard
// Walks admin through SPF → DKIM → DMARC → Postmaster with live DNS
// verification at each step. Plain English throughout.
// =====================================================================
type SetupStep = 1 | 2 | 3 | 4;

const GUIDE_STEPS = [
  {
    step: 1 as SetupStep,
    id: "spf" as const,
    title: "Email Domain Verification",
    technical: "SPF",
    description:
      "Tells Gmail, Yahoo, and Microsoft which servers are allowed to send email for your domain. Without this, your emails are far more likely to land in spam or get rejected outright.",
  },
  {
    step: 2 as SetupStep,
    id: "dkim" as const,
    title: "Message Signing Key",
    technical: "DKIM",
    description:
      "Adds an invisible digital signature to every email. Receiving servers check this signature to confirm the message really came from you and wasn't changed after you sent it.",
  },
  {
    step: 3 as SetupStep,
    id: "dmarc" as const,
    title: "Spam Protection Policy",
    technical: "DMARC",
    description:
      "Tells email providers what to do with emails that fail the two checks above — and requests reports showing who's sending under your domain name.",
  },
  {
    step: 4 as SetupStep,
    id: "postmaster" as const,
    title: "Gmail Reputation Monitor",
    technical: "Postmaster",
    description:
      "Google's free tool that shows your domain's spam complaint rate and reputation score — updated daily. The clearest signal of how Gmail views your sending domain.",
  },
] as const;

function parseRecommendedRecord(recommendation: string | undefined): { host: string | null; value: string | null } {
  if (!recommendation) return { host: null, value: null };
  const m = recommendation.match(/at\s+["']([^"']+)["']\s+with(?:\s+the)?\s+value:\s*(.+)$/i);
  if (m) return { host: m[1].trim(), value: m[2].trim() };
  const m2 = recommendation.match(/(?:Replace(?: the record)?|Use|Set)(?: to| with)?:?\s*(v=.+)$/i);
  if (m2) return { host: null, value: m2[1].trim() };
  return { host: null, value: null };
}

function AuthStatusBadge({ status }: { status: CheckStatus | "connected" | "unknown" }) {
  if (status === "pass" || status === "connected")
    return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200 dark:border-green-800"><CheckCircle2 className="h-3 w-3 mr-1" />{status === "connected" ? "Connected" : "Verified"}</Badge>;
  if (status === "warn")
    return <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800"><AlertTriangle className="h-3 w-3 mr-1" />Needs attention</Badge>;
  if (status === "fail")
    return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Not set up</Badge>;
  return <Badge variant="secondary"><Info className="h-3 w-3 mr-1" />Not checked yet</Badge>;
}

function EmailAuthSetupGuide({
  domain,
  dkimSelector,
  open,
  onOpenChange,
}: {
  domain: string;
  dkimSelector: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { toast } = useToast();
  const [step, setStep] = useState<SetupStep>(1);
  const [checkResult, setCheckResult] = useState<DomainCheckResult | null>(null);
  const [checking, setChecking] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const { data: pmData, refetch: refetchPm } = useQuery<PostmasterResponse>({
    queryKey: ["/api/admin/email-settings/postmaster"],
    enabled: open,
  });

  const connectPm = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/email-settings/postmaster/connect", {});
      return res.json() as Promise<{ authUrl: string }>;
    },
    onSuccess: (r) => { window.location.href = r.authUrl; },
    onError: (err: any) => toast({ title: "Failed to start connection", description: err?.message || String(err), variant: "destructive" }),
  });

  const runCheck = async () => {
    setChecking(true);
    try {
      const res = await apiRequest("POST", "/api/admin/email-settings/domains/check", { domain });
      const json = await res.json();
      setCheckResult(json.result);
    } catch (err: any) {
      toast({ title: "DNS check failed", description: err?.message || String(err), variant: "destructive" });
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (open) {
      setStep(1);
      setCheckResult(null);
      runCheck();
    }
  }, [open, domain]);

  const copyText = (text: string, key: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const stepStatus = (s: SetupStep): CheckStatus | "connected" | "unknown" => {
    if (!checkResult && s < 4) return "unknown";
    if (s === 1) return checkResult!.spf.status;
    if (s === 2) return checkResult!.dkim.status;
    if (s === 3) return checkResult!.dmarc.status;
    return pmData?.connected ? "connected" : "unknown";
  };

  const stepPassed = (s: SetupStep): boolean => {
    const st = (s <= 3 && checkResult) ? stepStatus(s) : (s === 4 ? (pmData?.connected ? "connected" : "unknown") : "unknown");
    return st === "pass" || st === "warn" || st === "connected";
  };

  const CopyBtn = ({ text, id }: { text: string; id: string }) => (
    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 shrink-0" onClick={() => copyText(text, id)} data-testid={`button-copy-${id}`}>
      {copied === id ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );

  const RecordBlock = ({ label, value, id }: { label: string; value: string; id: string }) => (
    <div className="space-y-1">
      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
      <div className="flex items-start gap-1.5">
        <code className="flex-1 text-xs bg-muted px-2 py-1.5 rounded break-all leading-relaxed">{value}</code>
        <CopyBtn text={value} id={id} />
      </div>
    </div>
  );

  const renderDnsStep = (id: "spf" | "dkim" | "dmarc") => {
    const result = checkResult?.[id];
    const status = result?.status ?? "unknown";
    const passed = status === "pass" || status === "warn";

    let host = "";
    let suggestedValue: string | null = null;

    if (id === "spf") {
      host = domain;
      if (!passed) {
        const p = parseRecommendedRecord(result?.recommendation);
        suggestedValue = p.value || `v=spf1 include:_spf.google.com ~all`;
      } else {
        suggestedValue = result?.record ?? null;
      }
    } else if (id === "dkim") {
      host = `${dkimSelector}._domainkey.${domain}`;
      suggestedValue = passed ? ((result as any)?.record ?? null) : null;
    } else {
      host = `_dmarc.${domain}`;
      if (!passed) {
        suggestedValue = `v=DMARC1; p=quarantine; pct=100; rua=mailto:dmarc-reports@${domain}`;
      } else {
        suggestedValue = result?.record ?? null;
      }
    }

    const dmarcRua = id === "dmarc" ? (checkResult?.dmarc.rua ?? null) : null;

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          {checking
            ? <span className="flex items-center gap-1.5 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Checking DNS…</span>
            : <><AuthStatusBadge status={status} />{result?.message && <span className="text-sm text-muted-foreground">{result.message}</span>}</>
          }
        </div>

        {passed && suggestedValue && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Current DNS record</p>
            <div className="flex items-start gap-1.5">
              <code className="flex-1 text-xs bg-muted px-2 py-1.5 rounded break-all leading-relaxed">{suggestedValue}</code>
              <CopyBtn text={suggestedValue} id={`${id}-record`} />
            </div>
          </div>
        )}

        {!passed && (
          <div className="rounded-lg border border-dashed p-4 space-y-3">
            <p className="text-sm font-medium">Add this to your DNS</p>
            <p className="text-xs text-muted-foreground">
              Log in to your domain registrar (GoDaddy, Cloudflare, Namecheap, Google Domains, etc.) and add a new DNS record with these values.
            </p>
            <div className="space-y-2">
              <RecordBlock label="Record type" value="TXT" id={`${id}-type`} />
              <RecordBlock label="Host / Name" value={host} id={`${id}-host`} />
              {suggestedValue && (
                <RecordBlock label="Value" value={suggestedValue} id={`${id}-value`} />
              )}
              {id === "dkim" && !suggestedValue && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    The DKIM value is generated by your email provider — not us. Find it in your Google Workspace admin (Apps → Gmail → Authenticate email) or Microsoft 365 admin and copy the TXT record value from there.
                  </AlertDescription>
                </Alert>
              )}
              {id === "dmarc" && (
                <p className="text-xs text-muted-foreground pt-1">
                  The <code className="bg-muted px-1 rounded">rua=</code> part tells providers where to email DMARC reports. Use any address you check — you'll receive XML report files from Gmail, Yahoo, and Microsoft. Once you receive them, you can upload them in the Domain Authentication tab to see your pass/fail stats inside Clarik.
                </p>
              )}
              <Alert className="mt-1">
                <Info className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  DNS changes usually appear within a few minutes, but can take up to 48 hours. Click <strong>Verify</strong> below after adding the record.
                </AlertDescription>
              </Alert>
            </div>
          </div>
        )}

        {id === "dmarc" && passed && (
          <div className="rounded-lg border p-3 space-y-1.5 text-xs">
            <p className="font-medium flex items-center gap-1.5"><BarChart3 className="h-3.5 w-3.5 text-muted-foreground" />DMARC reports</p>
            {dmarcRua ? (
              <p className="text-muted-foreground">
                Reports are being sent to <code className="bg-muted px-1 rounded">{dmarcRua}</code>. When you receive an email with a <code>.xml</code> or <code>.xml.gz</code> attachment, upload it in the Domain Authentication tab to see real-world pass/fail stats.
              </p>
            ) : (
              <p className="text-muted-foreground">
                Your DMARC record has no <code className="bg-muted px-1 rounded">rua=</code> tag, so you won't receive reports. Add{" "}
                <code className="bg-muted px-1 rounded">; rua=mailto:dmarc-reports@{domain}</code> to start receiving them, then upload the files here.
              </p>
            )}
          </div>
        )}

        <Button size="sm" variant="outline" onClick={runCheck} disabled={checking} data-testid={`button-guide-verify-${id}`}>
          {checking ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          {passed ? "Re-check" : "Verify"}
        </Button>
      </div>
    );
  };

  const renderPostmasterStep = () => {
    const connected = !!pmData?.connected;
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <AuthStatusBadge status={connected ? "connected" : "unknown"} />
          {connected && pmData?.accountEmail && (
            <span className="text-sm text-muted-foreground">as {pmData.accountEmail}</span>
          )}
        </div>

        {!connected && (
          <div className="rounded-lg border border-dashed p-4 space-y-3">
            <p className="text-sm font-medium">How to set this up</p>
            <ol className="text-xs text-muted-foreground space-y-2 list-decimal pl-4">
              <li>
                Go to{" "}
                <a href="https://postmaster.google.com" target="_blank" rel="noreferrer" className="text-primary underline inline-flex items-center gap-0.5" data-testid="link-postmaster-wizard">
                  postmaster.google.com <ExternalLink className="h-3 w-3" />
                </a>{" "}
                and sign in with a Google account that has access to your domain.
              </li>
              <li>
                Click <strong>Add domain</strong> and enter <strong>{domain}</strong>. Postmaster will show you a TXT record to add to your DNS — similar to what you did in steps 1–3.
              </li>
              <li>
                Once your domain is verified in Postmaster Tools, come back here and click <strong>Connect</strong>.
              </li>
              <li>
                Reputation data starts appearing after a few days of sending volume. Spam complaint rates and authentication pass rates update daily.
              </li>
            </ol>
          </div>
        )}

        {connected ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              You're all set. Reputation data syncs daily. Use the <strong>Google Postmaster</strong> tab to view your domain's spam rates and reputation over time.
            </p>
            <Button size="sm" onClick={() => onOpenChange(false)} data-testid="button-guide-done">
              Done — close guide
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <Button
              onClick={() => connectPm.mutate()}
              disabled={connectPm.isPending || !pmData?.configured}
              data-testid="button-guide-connect-postmaster"
            >
              {connectPm.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <LinkIcon className="h-4 w-4 mr-1" />}
              Connect Google Postmaster Tools
            </Button>
            {!pmData?.configured && (
              <p className="text-xs text-muted-foreground">Google OAuth is not configured on this server. Contact your platform admin.</p>
            )}
            <p className="text-xs text-muted-foreground">
              We only request read-only access — we never change anything in your Google account.
            </p>
          </div>
        )}
      </div>
    );
  };

  const meta = GUIDE_STEPS[step - 1];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="dialog-email-auth-guide">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Email Authentication Setup
          </DialogTitle>
          <DialogDescription>
            A one-time setup for <strong>{domain}</strong>. Complete all 4 steps to maximize email deliverability.
          </DialogDescription>
        </DialogHeader>

        {/* Progress stepper */}
        <div className="flex items-center gap-1">
          {GUIDE_STEPS.map((m, i) => {
            const s = m.step;
            const done = stepPassed(s);
            const active = step === s;
            return (
              <div key={m.id} className="flex items-center gap-1 flex-1">
                <button
                  onClick={() => setStep(s)}
                  className={cn(
                    "flex-1 flex items-center justify-center gap-1 py-1.5 px-1 rounded text-xs font-medium transition-colors truncate",
                    active
                      ? "bg-primary text-primary-foreground"
                      : done
                      ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 hover:opacity-80"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  )}
                  data-testid={`button-guide-step-${m.id}`}
                >
                  {done && !active
                    ? <CheckCircle2 className="h-3 w-3 shrink-0" />
                    : <span className="shrink-0 w-3.5 text-center">{s}</span>
                  }
                  <span className="hidden sm:inline truncate">{m.technical}</span>
                </button>
                {i < GUIDE_STEPS.length - 1 && <div className="h-px w-2 bg-border shrink-0" />}
              </div>
            );
          })}
        </div>

        {/* Step content */}
        <div className="space-y-3">
          <div>
            <h3 className="font-semibold text-base">{meta.title}</h3>
            <p className="text-[11px] text-muted-foreground font-mono uppercase tracking-wide">{meta.technical}</p>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">{meta.description}</p>

          {step === 1 && renderDnsStep("spf")}
          {step === 2 && renderDnsStep("dkim")}
          {step === 3 && renderDnsStep("dmarc")}
          {step === 4 && renderPostmasterStep()}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between pt-2 border-t">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setStep((prev) => Math.max(1, prev - 1) as SetupStep)}
            disabled={step === 1}
            data-testid="button-guide-back"
          >
            ← Back
          </Button>
          {step < 4 ? (
            <Button
              size="sm"
              onClick={() => setStep((prev) => Math.min(4, prev + 1) as SetupStep)}
              data-testid="button-guide-next"
            >
              Next: {GUIDE_STEPS[step].technical} →
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => onOpenChange(false)} data-testid="button-guide-finish">
              Close
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// =====================================================================
// Domain Authentication tab
// =====================================================================
function DomainAuthTab() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<{ domains: DomainListItem[] }>({
    queryKey: ["/api/admin/email-settings/domains"],
  });
  const { data: ruaData } = useQuery<{ summaries: DmarcAggregateSummaryItem[]; sinceDays: number }>({
    queryKey: ["/api/admin/email-settings/domains/rua"],
  });
  const ruaByDomain = new Map<string, DmarcAggregateSummaryItem>(
    (ruaData?.summaries || []).map((s) => [s.domain, s]),
  );
  const [results, setResults] = useState<Record<string, DomainCheckResult>>({});
  const [running, setRunning] = useState<Record<string, boolean>>({});
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);
  const [newDomain, setNewDomain] = useState("");
  const [newSelector, setNewSelector] = useState("google");
  const [guideOpen, setGuideOpen] = useState(false);
  const [guideDomain, setGuideDomain] = useState<{ domain: string; dkimSelector: string } | null>(null);

  const openGuide = (domain: string, dkimSelector: string) => {
    setGuideDomain({ domain, dkimSelector });
    setGuideOpen(true);
  };

  const uploadDmarcReport = async (domain: string, file: File) => {
    setUploadingFor(domain);
    try {
      const buf = await file.arrayBuffer();
      const isGz = file.name.toLowerCase().endsWith(".gz") || file.type === "application/gzip";
      const res = await fetch("/api/admin/email-settings/domains/rua", {
        method: "POST",
        credentials: "include",
        headers: await withCsrfHeader({
          "Content-Type": isGz ? "application/gzip" : "application/xml",
        }),
        body: buf,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.message || `Upload failed (${res.status})`);
      }
      const json = await res.json();
      toast({
        title: "DMARC report ingested",
        description: `${json.report?.totalMessages ?? 0} messages from ${json.report?.orgName || "reporter"}.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-settings/domains/rua"] });
    } catch (err: any) {
      toast({ title: "Could not ingest report", description: err?.message || String(err), variant: "destructive" });
    } finally {
      setUploadingFor(null);
    }
  };

  const runOne = async (domain: string) => {
    setRunning(prev => ({ ...prev, [domain]: true }));
    try {
      const res = await apiRequest("POST", "/api/admin/email-settings/domains/check", { domain });
      const json = await res.json();
      setResults(prev => ({ ...prev, [domain]: json.result }));
    } catch (err: any) {
      toast({ title: "DNS check failed", description: err?.message || String(err), variant: "destructive" });
    } finally {
      setRunning(prev => ({ ...prev, [domain]: false }));
    }
  };

  const runAll = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/email-settings/domains/check", { all: true });
      return res.json() as Promise<{ results: DomainCheckResult[] }>;
    },
    onSuccess: (data) => {
      const map: Record<string, DomainCheckResult> = {};
      data.results.forEach(r => { map[r.domain] = r; });
      setResults(map);
      toast({ title: "DNS check complete", description: `Checked ${data.results.length} domain(s).` });
    },
    onError: (err: any) => toast({ title: "DNS check failed", description: err?.message || String(err), variant: "destructive" }),
  });

  const addDomain = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/email-settings/domains", {
        domain: newDomain.trim().toLowerCase(),
        dkimSelector: newSelector.trim() || "google",
      });
      return res.json();
    },
    onSuccess: () => {
      setNewDomain("");
      setNewSelector("google");
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-settings/domains"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-settings/overview"] });
      toast({ title: "Domain added" });
    },
    onError: (err: any) => toast({ title: "Failed to add domain", description: err?.message || String(err), variant: "destructive" }),
  });

  const deleteDomain = useMutation({
    mutationFn: async (domain: string) => apiRequest("DELETE", `/api/admin/email-settings/domains/by-domain`, { domain }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-settings/domains"] });
      toast({ title: "Domain removed" });
    },
    onError: (err: any) => toast({ title: "Failed to remove", description: err?.message || String(err), variant: "destructive" }),
  });

  if (isLoading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  const domains = data?.domains || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Domain Authentication</h2>
          <p className="text-sm text-muted-foreground">SPF, DKIM and DMARC are how Gmail and Yahoo know your mail is really yours. Run a check to see what each domain is publishing in DNS right now.</p>
        </div>
        <Button size="sm" onClick={() => runAll.mutate()} disabled={runAll.isPending || domains.length === 0} data-testid="button-run-all-checks">
          {runAll.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Run all checks
        </Button>
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Read-only — we never change DNS for you.</AlertTitle>
        <AlertDescription className="text-xs">
          We look up the public DNS records and tell you whether they look healthy. To fix a record, you (or your IT) need to log in to your domain registrar.
        </AlertDescription>
      </Alert>

      <div className="space-y-3">
        {domains.length === 0 && (
          <Card><CardContent className="py-6 text-center text-sm text-muted-foreground">
            No sending domains found. Connect a Gmail mailbox or add a domain manually below.
          </CardContent></Card>
        )}
        {domains.map((d) => {
          const result = results[d.domain];
          const isRunning = !!running[d.domain];
          return (
            <Card key={d.domain} data-testid={`card-domain-${d.domain}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <CardTitle className="text-sm" data-testid={`text-domain-name-${d.domain}`}>{d.domain}</CardTitle>
                    <CardDescription className="text-xs">
                      <Badge variant="outline" className="mr-1 text-[10px]">{d.source === "auto" ? "from a connected mailbox" : "added manually"}</Badge>
                      DKIM selector: <code>{d.dkimSelector}</code>
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button size="sm" variant="default" onClick={() => openGuide(d.domain, d.dkimSelector)} data-testid={`button-setup-guide-${d.domain}`}>
                      <ShieldCheck className="h-3.5 w-3.5 mr-1" />
                      Setup guide
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => runOne(d.domain)} disabled={isRunning} data-testid={`button-check-${d.domain}`}>
                      {isRunning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                      Check now
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (confirm(`Remove ${d.domain}?`)) {
                          deleteDomain.mutate(d.domain);
                        }
                      }}
                      disabled={deleteDomain.isPending}
                      data-testid={`button-remove-${d.domain}`}
                      title="Remove this domain"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {result && (
                <CardContent className="space-y-2 text-xs">
                  {[
                    { key: "spf", label: "SPF", c: result.spf },
                    { key: "dkim", label: "DKIM", c: result.dkim },
                    { key: "dmarc", label: "DMARC", c: result.dmarc },
                  ].map(({ key, label, c }) => (
                    <div key={key} className="border rounded-md p-2 space-y-1" data-testid={`row-check-${d.domain}-${key}`}>
                      <div className="flex items-center gap-2">
                        <StatusIcon status={c.status} />
                        <span className="font-medium">{label}</span>
                        <span className="text-muted-foreground">— {c.message}</span>
                      </div>
                      {c.record && (
                        <code className="block text-[10px] bg-muted p-1 rounded break-all">{c.record}</code>
                      )}
                      {c.recommendation && (
                        <p className="text-amber-800 dark:text-amber-200 text-[11px]">→ {c.recommendation}</p>
                      )}
                    </div>
                  ))}
                  <p className="text-[10px] text-muted-foreground">Checked {new Date(result.checkedAt).toLocaleString()}</p>
                </CardContent>
              )}
              <DmarcAggregateSection
                domain={d.domain}
                summary={ruaByDomain.get(d.domain) || null}
                onUpload={(file) => uploadDmarcReport(d.domain, file)}
                isUploading={uploadingFor === d.domain}
              />
            </Card>
          );
        })}
      </div>

      <Card data-testid="card-add-domain">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Add a domain manually</CardTitle>
          <CardDescription className="text-xs">If your team sends from a domain that isn't connected as a mailbox yet (or you're auditing one before connecting), add it here.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col sm:flex-row gap-2">
          <Input placeholder="example.com" value={newDomain} onChange={e => setNewDomain(e.target.value)} data-testid="input-new-domain" />
          <Input placeholder="DKIM selector (default: google)" value={newSelector} onChange={e => setNewSelector(e.target.value)} className="sm:max-w-[220px]" data-testid="input-dkim-selector" />
          <Button onClick={() => addDomain.mutate()} disabled={!newDomain.trim() || addDomain.isPending} data-testid="button-add-domain">
            {addDomain.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
            Add
          </Button>
        </CardContent>
      </Card>

      {guideDomain && (
        <EmailAuthSetupGuide
          domain={guideDomain.domain}
          dkimSelector={guideDomain.dkimSelector}
          open={guideOpen}
          onOpenChange={setGuideOpen}
        />
      )}
    </div>
  );
}

// =====================================================================
// Mass Email Compliance tab
// =====================================================================
type ComplianceVariant = "identity" | "canspam" | "full";

function ComplianceTab({ variant = "full" }: { variant?: ComplianceVariant } = {}) {
  const showIdentity = variant === "identity" || variant === "full";
  const showCanspam = variant === "canspam" || variant === "full";
  const { toast } = useToast();
  const { data, isLoading } = useQuery<ComplianceResponse>({
    queryKey: ["/api/admin/email-settings/compliance"],
  });
  // Resolve the active workspace's brand for the sender-name input
  // placeholder (customEmailFromName -> workspace.name -> Clarik)
  // so other tenants don't see the original agency literal as a hint
  // when first configuring mass-email compliance.
  const { workspaceSlug } = useWorkspace();
  const { data: branding } = useQuery<{
    workspaceName: string | null;
    customEmailFromName: string | null;
  }>({
    queryKey: ["/api/branding/public", workspaceSlug],
  });
  const senderNamePlaceholder =
    branding?.customEmailFromName?.trim() ||
    branding?.workspaceName?.trim() ||
    "Clarik";
  const [form, setForm] = useState({
    senderName: "",
    senderEmail: "",
    replyToEmail: "",
    physicalAddress: "",
    unsubscribeFooterHtml: "",
  });
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (data?.massEmail && !initialized) {
      setForm({
        senderName: data.massEmail.senderName || "",
        senderEmail: data.massEmail.senderEmail || "",
        replyToEmail: data.massEmail.replyToEmail || "",
        physicalAddress: data.massEmail.physicalAddress || "",
        unsubscribeFooterHtml: data.massEmail.unsubscribeFooterHtml || "",
      });
      setInitialized(true);
    }
  }, [data, initialized]);

  const save = useMutation({
    mutationFn: async () => {
      // PATCH only the subset this variant owns. The server partial-merges
      // (skips undefined), so the identity form never clobbers the CAN-SPAM
      // fields and vice-versa — letting the two live on different surfaces.
      const payload =
        variant === "identity"
          ? {
              senderName: form.senderName,
              senderEmail: form.senderEmail,
              replyToEmail: form.replyToEmail,
            }
          : variant === "canspam"
            ? {
                physicalAddress: form.physicalAddress,
                unsubscribeFooterHtml: form.unsubscribeFooterHtml,
              }
            : form;
      const res = await apiRequest("PATCH", "/api/admin/email-settings/compliance", payload);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-settings/compliance"] });
      toast({ title: "Compliance settings saved" });
    },
    onError: (err: any) => toast({ title: "Failed to save", description: err?.message || String(err), variant: "destructive" }),
  });

  if (isLoading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const compliantOk = !!(form.senderName && form.physicalAddress && form.unsubscribeFooterHtml);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">
          {variant === "identity"
            ? "Sender identity"
            : variant === "canspam"
              ? "Cold email compliance"
              : "Mass Email Compliance"}
        </h2>
        <p className="text-sm text-muted-foreground">
          {variant === "identity"
            ? "The from-name and addresses recipients see on your campaigns."
            : "Required pieces for CAN-SPAM (US) and Gmail/Yahoo's bulk-sender rules. These get added to every campaign automatically."}
        </p>
      </div>

      {showCanspam && (
        <Alert variant={compliantOk ? "default" : "destructive"} data-testid="alert-compliance-status">
          {compliantOk ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          <AlertTitle>{compliantOk ? "All required pieces are set." : "One or more required fields are missing."}</AlertTitle>
          <AlertDescription className="text-xs">
            Required: a sender name, a physical mailing address, and an unsubscribe footer. Reply-to is recommended.
          </AlertDescription>
        </Alert>
      )}

      {showIdentity && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Sender identity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label htmlFor="senderName">Sender name <HelpTip text="The friendly name recipients see before your email address." /></Label>
              <Input id="senderName" value={form.senderName} onChange={e => setForm(f => ({ ...f, senderName: e.target.value }))} placeholder={senderNamePlaceholder} data-testid="input-sender-name" />
            </div>
            <div>
              <Label htmlFor="senderEmail">Default sender email <HelpTip text="Used when a campaign doesn't specify one. Most campaigns pick a connected mailbox at send time." /></Label>
              <Input id="senderEmail" type="email" value={form.senderEmail} onChange={e => setForm(f => ({ ...f, senderEmail: e.target.value }))} placeholder="hello@example.com" data-testid="input-sender-email" />
            </div>
            <div>
              <Label htmlFor="replyTo">Reply-to address <HelpTip text="Where replies should land if you don't want them in the sending mailbox." /></Label>
              <Input id="replyTo" type="email" value={form.replyToEmail} onChange={e => setForm(f => ({ ...f, replyToEmail: e.target.value }))} placeholder="replies@example.com" data-testid="input-reply-to" />
            </div>
          </CardContent>
        </Card>
      )}

      {showCanspam && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">CAN-SPAM footer pieces</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label htmlFor="address">Physical mailing address <HelpTip text="CAN-SPAM requires a real physical address (street, city, state, zip) in every commercial email." /></Label>
              <Textarea id="address" value={form.physicalAddress} onChange={e => setForm(f => ({ ...f, physicalAddress: e.target.value }))} placeholder="123 Main St, Suite 4&#10;Austin, TX 78701" rows={3} data-testid="input-physical-address" />
            </div>
            <div>
              <Label htmlFor="footer">Unsubscribe footer (HTML allowed) <HelpTip text="The exact wording and link appended to the bottom of every campaign." /></Label>
              <Textarea id="footer" value={form.unsubscribeFooterHtml} onChange={e => setForm(f => ({ ...f, unsubscribeFooterHtml: e.target.value }))} placeholder='You received this because you booked with us. <a href="{{unsubscribeUrl}}">Unsubscribe</a>.' rows={4} data-testid="input-unsubscribe-footer" />
              <p className="text-xs text-muted-foreground mt-1">Use <code>{`{{unsubscribeUrl}}`}</code> for the personalized opt-out link.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {showCanspam && (
        <Card data-testid="card-suppression-summary">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Suppression list</CardTitle>
            <CardDescription className="text-xs">People who have unsubscribed, hard-bounced, or been manually suppressed. They will never receive a campaign.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Total suppressed</span><span data-testid="text-suppression-total">{data?.suppression.total ?? 0}</span></div>
            {data && Object.entries(data.suppression.byReason).map(([reason, count]) => (
              <div key={reason} className="flex justify-between text-xs"><span className="text-muted-foreground capitalize">{reason.replace(/_/g, " ")}</span><span>{count}</span></div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="button-save-compliance">
          {save.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
          {variant === "identity" ? "Save sender identity" : "Save compliance settings"}
        </Button>
      </div>
    </div>
  );
}

// =====================================================================
// Alerts & Notifications tab
// =====================================================================
function AlertsTab() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<AlertsResponse>({
    queryKey: ["/api/admin/email-settings/alerts"],
  });
  const [form, setForm] = useState<Partial<EmailAlertSettings>>({});
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (data?.settings && !initialized) {
      setForm(data.settings);
      setInitialized(true);
    }
  }, [data, initialized]);

  const save = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PATCH", "/api/admin/email-settings/alerts", form);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-settings/alerts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-settings/overview"] });
      toast({ title: "Alert settings saved" });
    },
    onError: (err: any) => toast({ title: "Failed to save", description: err?.message || String(err), variant: "destructive" }),
  });

  const evaluate = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/email-settings/alerts/evaluate", {});
      return res.json();
    },
    onSuccess: (r: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-settings/alerts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-settings/overview"] });
      toast({ title: "Health check complete", description: `Created ${r.alertsCreated} new alert${r.alertsCreated === 1 ? "" : "s"}.` });
    },
    onError: (err: any) => toast({ title: "Check failed", description: err?.message || String(err), variant: "destructive" }),
  });

  const resolve = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/admin/email-settings/alerts/${id}/resolve`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-settings/alerts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-settings/overview"] });
    },
  });

  if (isLoading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!data) return <p className="text-sm text-muted-foreground">No data available.</p>;

  const PctInput = ({ field, label, help }: { field: keyof EmailAlertSettings; label: string; help: string }) => {
    const v = (form[field] as number | undefined) ?? 0;
    return (
      <div>
        <Label className="text-xs flex items-center gap-1">{label} <HelpTip text={help} /></Label>
        <div className="flex items-center gap-2">
          <Input type="number" step="0.1" min="0" max="100" value={(v * 100).toFixed(2)}
            onChange={e => setForm(f => ({ ...f, [field]: Math.max(0, Math.min(1, parseFloat(e.target.value || "0") / 100)) }))}
            className="max-w-[120px]" data-testid={`input-${String(field)}`} />
          <span className="text-xs text-muted-foreground">%</span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Alerts & Notifications</h2>
          <p className="text-sm text-muted-foreground">When something looks off, we'll surface it here and (optionally) email the workspace owner.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => evaluate.mutate()} disabled={evaluate.isPending} data-testid="button-run-health-check">
          {evaluate.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <BellRing className="h-4 w-4 mr-1" />}
          Run health check now
        </Button>
      </div>

      <Card data-testid="card-active-alerts">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Active alerts ({data.active.length})</CardTitle>
          <CardDescription className="text-xs">Each unresolved alert is shown here until you mark it resolved.</CardDescription>
        </CardHeader>
        <CardContent>
          {data.active.length === 0 ? (
            <p className="text-sm text-muted-foreground">No active alerts. Things look good.</p>
          ) : (
            <div className="space-y-2">
              {data.active.map(a => (
                <div key={a.id} className="flex items-start gap-2 border rounded-md p-2" data-testid={`row-active-alert-${a.id}`}>
                  {a.severity === "atRisk" ? <XCircle className="h-4 w-4 text-destructive mt-0.5" /> : <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />}
                  <div className="flex-1 text-sm">
                    <p>{a.message}</p>
                    <p className="text-xs text-muted-foreground">{new Date(a.createdAt!).toLocaleString()}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => resolve.mutate(a.id)} data-testid={`button-resolve-${a.id}`}>Mark resolved</Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <details className="group rounded-md border" data-testid="details-alerting-rules">
        <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium flex items-center gap-2">
          <Settings2 className="h-4 w-4 text-muted-foreground" />
          Alerting rules &amp; thresholds
          <span className="text-xs text-muted-foreground font-normal ml-auto group-open:hidden">Show</span>
          <span className="text-xs text-muted-foreground font-normal ml-auto hidden group-open:inline">Hide</span>
        </summary>
        <div className="space-y-4 p-3 pt-0">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Watch thresholds</CardTitle>
          <CardDescription className="text-xs">These are intentionally stricter than the per-mailbox auto-pause limits, so you hear about a problem before sending actually pauses.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <PctInput field="watchBounceRate24h" label="24h bounce rate" help="Alert when a mailbox's 24-hour bounce rate exceeds this." />
          <PctInput field="watchBounceRate7d" label="7-day bounce rate" help="Alert when a mailbox's 7-day rolling bounce rate exceeds this." />
          <PctInput field="watchOptOutRate7d" label="7-day opt-out rate" help="Alert when more than this percentage of recipients unsubscribe over a rolling week." />
          <PctInput field="watchSpamComplaintRate" label="Spam complaint rate" help="Reported by Postmaster Tools. Gmail's red line is 0.3%; aim well below 0.1%." />
          <PctInput field="watchOpenRateFloor" label="Open-rate floor" help="Alert if the open rate drops below this — could mean inbox placement is suffering." />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">What we alert on</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Reputation drops in Postmaster Tools</Label>
              <p className="text-xs text-muted-foreground">Triggers when Google reports your domain reputation is now LOW or BAD.</p>
            </div>
            <Switch checked={form.alertOnReputationDrop ?? true} onCheckedChange={v => setForm(f => ({ ...f, alertOnReputationDrop: v }))} data-testid="switch-alert-reputation" />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Missing DMARC record</Label>
              <p className="text-xs text-muted-foreground">Triggers nightly if any sending domain has no DMARC published.</p>
            </div>
            <Switch checked={form.alertOnDmarcMissing ?? true} onCheckedChange={v => setForm(f => ({ ...f, alertOnDmarcMissing: v }))} data-testid="switch-alert-dmarc" />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Mailbox auto-paused</Label>
              <p className="text-xs text-muted-foreground">Triggers when a mailbox crosses an auto-pause threshold.</p>
            </div>
            <Switch checked={form.alertOnMailboxPaused ?? true} onCheckedChange={v => setForm(f => ({ ...f, alertOnMailboxPaused: v }))} data-testid="switch-alert-paused" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Where alerts go</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Show banner inside the app</Label>
              <p className="text-xs text-muted-foreground">Active alerts always appear on the Overview tab. This adds a banner across the app.</p>
            </div>
            <Switch checked={form.inAppBannerEnabled ?? true} onCheckedChange={v => setForm(f => ({ ...f, inAppBannerEnabled: v }))} data-testid="switch-banner" />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Email the workspace owner</Label>
              <p className="text-xs text-muted-foreground">Send a digest email when a new alert opens.</p>
            </div>
            <Switch checked={form.emailWorkspaceOwner ?? true} onCheckedChange={v => setForm(f => ({ ...f, emailWorkspaceOwner: v }))} data-testid="switch-email-owner" />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="button-save-alerts">
          {save.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
          Save alert settings
        </Button>
      </div>
        </div>
      </details>
    </div>
  );
}

// =====================================================================
// Postmaster sub-tab (lives inside the Overview tab area as a section)
// =====================================================================
function PostmasterSection() {
  const { toast } = useToast();
  const { data, isLoading, refetch } = useQuery<PostmasterResponse>({
    queryKey: ["/api/admin/email-settings/postmaster"],
  });
  // Query parameter must live INSIDE the single key segment because the
  // default queryFn builds the URL via `queryKey.join("/")` — splitting
  // `30` into its own segment would request `.../history/30` and 404.
  const { data: history } = useQuery<PostmasterHistoryResponse>({
    queryKey: ["/api/admin/email-settings/postmaster/history?days=30"],
    enabled: !!data?.connected,
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("postmaster");
    if (status === "success") {
      toast({ title: "Google Postmaster Tools connected" });
      params.delete("postmaster");
      const search = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (search ? `?${search}` : ""));
      refetch();
    } else if (status === "error") {
      toast({ title: "Postmaster connection failed", description: params.get("reason") || "Unknown error", variant: "destructive" });
      params.delete("postmaster");
      params.delete("reason");
      const search = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (search ? `?${search}` : ""));
    }
  }, [toast, refetch]);

  const connect = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/email-settings/postmaster/connect", {});
      return res.json() as Promise<{ authUrl: string }>;
    },
    onSuccess: (r) => { window.location.href = r.authUrl; },
    onError: (err: any) => toast({ title: "Failed to start connection", description: err?.message || String(err), variant: "destructive" }),
  });

  const sync = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/email-settings/postmaster/sync", {});
      return res.json();
    },
    onSuccess: () => {
      // The sync route now also back-fills 30 days of reputation history,
      // so refresh the trend cards and the overview worst-signal card on
      // success — otherwise the user would have to reload to see new data.
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-settings/postmaster"] });
      // Match the exact query key used by `useQuery` above (the `?days=30`
      // suffix is part of the key string because the default queryFn joins
      // segments with "/").
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-settings/postmaster/history?days=30"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-settings/overview"] });
      toast({ title: "Postmaster data refreshed" });
    },
    onError: (err: any) => toast({ title: "Sync failed", description: err?.message || String(err), variant: "destructive" }),
  });

  const disconnect = useMutation({
    mutationFn: async () => apiRequest("DELETE", "/api/admin/email-settings/postmaster"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-settings/postmaster"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-settings/overview"] });
      toast({ title: "Disconnected from Google Postmaster Tools" });
    },
    onError: (err: any) => toast({ title: "Disconnect failed", description: err?.message || String(err), variant: "destructive" }),
  });

  if (isLoading) return <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!data) return null;

  const cached: any[] = Array.isArray(data.cachedDomains) ? data.cachedDomains : [];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Google Postmaster Tools</h2>
        <p className="text-sm text-muted-foreground">Read-only view of how Gmail rates your domains. Connect to see reputation, spam-rate, SPF/DKIM/DMARC pass rates.</p>
      </div>

      {!data.configured && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Server not configured</AlertTitle>
          <AlertDescription className="text-xs">Set <code>GOOGLE_CLIENT_ID</code> and <code>GOOGLE_CLIENT_SECRET</code> on the server before connecting.</AlertDescription>
        </Alert>
      )}

      {data.configured && !data.connected && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Connect read-only access</CardTitle>
            <CardDescription className="text-xs">We'll only request Postmaster Tools' read-only scope. We never change anything in Google.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.walkthrough && (
              <div className="text-xs space-y-2">
                <p className="font-medium">Before you connect, verify your sending domain inside Postmaster Tools:</p>
                <ol className="list-decimal pl-5 space-y-1 text-muted-foreground">
                  {data.walkthrough.steps.map((step, i) => <li key={i}>{step}</li>)}
                </ol>
                <p className="text-muted-foreground italic">{data.walkthrough.note}</p>
                <a href="https://postmaster.google.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary underline" data-testid="link-postmaster">
                  Open Postmaster Tools <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            )}
            <Button onClick={() => connect.mutate()} disabled={connect.isPending} data-testid="button-connect-postmaster">
              {connect.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              <LinkIcon className="h-4 w-4 mr-1" />
              Connect Google Postmaster Tools
            </Button>
          </CardContent>
        </Card>
      )}

      {data.connected && (
        <>
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-sm">Connected{data.accountEmail ? ` as ${data.accountEmail}` : ""}</CardTitle>
                  <CardDescription className="text-xs">
                    {data.lastSyncedAt ? `Last sync: ${new Date(data.lastSyncedAt).toLocaleString()}` : "Not synced yet."}
                    {data.lastSyncError && <span className="block text-destructive">Error: {data.lastSyncError}</span>}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => sync.mutate()} disabled={sync.isPending} data-testid="button-sync-postmaster">
                    {sync.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                    Refresh
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => disconnect.mutate()} disabled={disconnect.isPending} data-testid="button-disconnect-postmaster">
                    <Unlink className="h-4 w-4 mr-1" /> Disconnect
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          {cached.length > 0 && (
            <div className="space-y-3">
              {cached.map((row: any) => (
                <Card key={row.domain} data-testid={`card-postmaster-domain-${row.domain}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      {row.domain}
                      {row.error === "not_verified" && <Badge variant="outline" className="text-[10px]">Not verified in Postmaster Tools</Badge>}
                      {row.reputation && <Badge variant={row.reputation === "HIGH" ? "default" : row.reputation === "MEDIUM" ? "secondary" : "destructive"}>{row.reputation}</Badge>}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-xs">
                    {row.error && row.error !== "not_verified" && (
                      <p className="text-destructive">{row.error}</p>
                    )}
                    {row.error === "not_verified" && (
                      <p className="text-muted-foreground">Add and verify this domain inside Postmaster Tools to start collecting data.</p>
                    )}
                    {!row.error && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <div><span className="text-muted-foreground">Spam reports</span><p>{row.spamRate !== null ? formatPct(row.spamRate, 3) : "—"}</p></div>
                        <div><span className="text-muted-foreground">SPF pass</span><p>{row.spfPassRate !== null ? formatPct(row.spfPassRate) : "—"}</p></div>
                        <div><span className="text-muted-foreground">DKIM pass</span><p>{row.dkimPassRate !== null ? formatPct(row.dkimPassRate) : "—"}</p></div>
                        <div><span className="text-muted-foreground">DMARC pass</span><p>{row.dmarcPassRate !== null ? formatPct(row.dmarcPassRate) : "—"}</p></div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {cached.length === 0 && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>No cached data yet.</AlertTitle>
              <AlertDescription className="text-xs">Click <em>Refresh</em> above to pull the latest snapshots from Google.</AlertDescription>
            </Alert>
          )}

          {history && history.series.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                <h3 className="text-sm font-semibold">Last 30 days · trends</h3>
              </div>
              {history.worstSignal && (
                <Alert
                  variant={history.worstSignal.severity === "atRisk" ? "destructive" : "default"}
                  className={history.worstSignal.severity === "watch" ? "border-amber-500/50 bg-amber-500/5" : undefined}
                  data-testid={`alert-postmaster-worst-${history.worstSignal.severity}`}
                >
                  {history.worstSignal.severity === "atRisk" && <XCircle className="h-4 w-4" />}
                  {history.worstSignal.severity === "watch" && <AlertTriangle className="h-4 w-4 text-amber-600" />}
                  {history.worstSignal.severity === "ok" && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                  {history.worstSignal.severity === "unknown" && <Info className="h-4 w-4" />}
                  <AlertTitle className="text-sm">Worst current signal</AlertTitle>
                  <AlertDescription className="text-xs">{history.worstSignal.message}</AlertDescription>
                </Alert>
              )}
              {history.series.map((s) => (
                <PostmasterTrendCard key={s.domain} series={s} />
              ))}
            </div>
          )}

          {history && history.series.length === 0 && cached.length > 0 && (
            <Alert data-testid="alert-postmaster-history-empty">
              <Info className="h-4 w-4" />
              <AlertTitle>No trend data yet.</AlertTitle>
              <AlertDescription className="text-xs">Trend charts will populate after the daily sync runs (or click <em>Refresh</em> to backfill the last 30 days now).</AlertDescription>
            </Alert>
          )}
        </>
      )}
    </div>
  );
}

// One card per domain — shows reputation + spam-rate + delivery-error trends
// alongside auth pass-rate trends (SPF/DKIM/DMARC).
function PostmasterTrendCard({ series }: { series: PostmasterHistorySeries }) {
  const repPoints = series.points.map((p) => ({
    date: p.date.slice(5), // MM-DD
    reputation: reputationToScore(p.domainReputation),
    spamPct: p.spamRate !== null ? +(p.spamRate * 100).toFixed(3) : null,
    errorPct: p.deliveryErrorRate !== null ? +(p.deliveryErrorRate * 100).toFixed(2) : null,
  }));
  const authPoints = series.points.map((p) => ({
    date: p.date.slice(5),
    spfPct: p.spfPassRate !== null ? +(p.spfPassRate * 100).toFixed(1) : null,
    dkimPct: p.dkimPassRate !== null ? +(p.dkimPassRate * 100).toFixed(1) : null,
    dmarcPct: p.dmarcPassRate !== null ? +(p.dmarcPassRate * 100).toFixed(1) : null,
  }));
  const ipPoints = series.points.map((p) => ({
    date: p.date.slice(5),
    highPct: p.ipShares.HIGH !== null ? +(p.ipShares.HIGH * 100).toFixed(1) : null,
    mediumPct: p.ipShares.MEDIUM !== null ? +(p.ipShares.MEDIUM * 100).toFixed(1) : null,
    lowPct: p.ipShares.LOW !== null ? +(p.ipShares.LOW * 100).toFixed(1) : null,
    badPct: p.ipShares.BAD !== null ? +(p.ipShares.BAD * 100).toFixed(1) : null,
  }));
  const hasIpData = ipPoints.some(p => p.highPct !== null || p.mediumPct !== null || p.lowPct !== null || p.badPct !== null);

  // Typed recharts tooltip formatters — recharts ValueType is (string | number | (string|number)[]).
  const toScalarNumber = (v: RechartsValue): number | undefined => {
    if (typeof v === "number") return v;
    if (typeof v === "string") { const n = Number(v); return Number.isFinite(n) ? n : undefined; }
    return undefined;
  };
  const reputationFormatter = (v: RechartsValue): [string, string] => [scoreToReputationLabel(toScalarNumber(v)), "Domain reputation"];
  const percentLabelFormatter = (v: RechartsValue, name: string): [string, string] => {
    const display = Array.isArray(v) ? v.join(", ") : String(v);
    return [`${display}%`, name];
  };
  const percentOnlyFormatter = (v: RechartsValue): [string, string] => {
    const display = Array.isArray(v) ? v.join(", ") : String(v);
    return [`${display}%`, ""];
  };

  return (
    <Card data-testid={`card-postmaster-trend-${series.domain}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{series.domain}</CardTitle>
        <CardDescription className="text-xs">{series.points.length} day{series.points.length === 1 ? "" : "s"} of Google Postmaster data</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-xs font-medium mb-1 text-muted-foreground">Domain reputation (HIGH=3 · MEDIUM=2 · LOW=1 · BAD=0)</p>
          <div className="h-32 w-full" data-testid={`chart-postmaster-reputation-${series.domain}`}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={repPoints}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis domain={[0, 3]} ticks={[0, 1, 2, 3]} tickFormatter={(v) => scoreToReputationLabel(v)} tick={{ fontSize: 10 }} width={60} />
                <RechartsTooltip formatter={reputationFormatter} />
                <Line type="monotone" dataKey="reputation" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {hasIpData && (
          <div>
            <p className="text-xs font-medium mb-1 text-muted-foreground">Sending IP reputation (% of IPs in each bucket)</p>
            <div className="h-32 w-full" data-testid={`chart-postmaster-ip-reputation-${series.domain}`}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={ipPoints}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} width={45} />
                  <RechartsTooltip formatter={percentLabelFormatter} />
                  <Line type="monotone" dataKey="highPct" name="HIGH" stroke="#10b981" strokeWidth={2} dot={false} connectNulls />
                  <Line type="monotone" dataKey="mediumPct" name="MEDIUM" stroke="#3b82f6" strokeWidth={2} dot={false} connectNulls />
                  <Line type="monotone" dataKey="lowPct" name="LOW" stroke="#f59e0b" strokeWidth={2} dot={false} connectNulls />
                  <Line type="monotone" dataKey="badPct" name="BAD" stroke="#dc2626" strokeWidth={2} dot={false} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div>
          <p className="text-xs font-medium mb-1 text-muted-foreground">Spam complaints &amp; delivery errors (% of mail)</p>
          <div className="h-32 w-full" data-testid={`chart-postmaster-spam-${series.domain}`}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={repPoints}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} width={45} />
                <RechartsTooltip formatter={(v: RechartsValue, name: string) => percentLabelFormatter(v, name === "spamPct" ? "Spam reports" : "Delivery errors")} />
                <Line type="monotone" dataKey="spamPct" name="Spam reports" stroke="#dc2626" strokeWidth={2} dot={false} connectNulls />
                <Line type="monotone" dataKey="errorPct" name="Delivery errors" stroke="#f59e0b" strokeWidth={2} dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div>
          <p className="text-xs font-medium mb-1 text-muted-foreground">Authentication pass rates (SPF / DKIM / DMARC)</p>
          <div className="h-32 w-full" data-testid={`chart-postmaster-auth-${series.domain}`}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={authPoints}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} width={45} />
                <RechartsTooltip formatter={percentOnlyFormatter} />
                <Line type="monotone" dataKey="spfPct" name="SPF" stroke="#10b981" strokeWidth={2} dot={false} connectNulls />
                <Line type="monotone" dataKey="dkimPct" name="DKIM" stroke="#3b82f6" strokeWidth={2} dot={false} connectNulls />
                <Line type="monotone" dataKey="dmarcPct" name="DMARC" stroke="#8b5cf6" strokeWidth={2} dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// =====================================================================
// Top-level dialog
// =====================================================================
interface IngestUser {
  userId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
}

interface IngestStateRow {
  id: string;
  userId: string;
  mode: string;
  status: string;
  recipientEmail: string | null;
  sinceDate: string | null;
  untilDate: string | null;
  processedCount: number | null;
  matchedCount: number | null;
  lastError: string | null;
  startedAt: string | null;
  completedAt: string | null;
}

const INGEST_RANGE_PRESETS = [
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
  { label: "Last 6 months", days: 183 },
  { label: "Last 12 months", days: 365 },
  { label: "Since 2025-01-01", days: 0, fixed: "2025-01-01" },
] as const;

function daysAgoIso(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

function formatIngestUserLabel(u: IngestUser): string {
  const name = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  const email = u.email || "(no email)";
  return name ? `${name} — ${email}` : email;
}

function IngestTab() {
  const { toast } = useToast();
  const usersQ = useQuery<{ users: IngestUser[] }>({
    queryKey: ["/api/admin/email-settings/gmail-connected-users"],
  });
  const statusQ = useQuery<{ rows: IngestStateRow[] }>({
    queryKey: ["/api/admin/email-settings/ingest-historical/status"],
    refetchInterval: 5000,
  });

  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [presetIdx, setPresetIdx] = useState<number>(0);
  const [customDate, setCustomDate] = useState<string>("");
  const usingCustom = customDate.trim().length > 0;

  const sinceDate = usingCustom
    ? customDate
    : (() => {
        const p = INGEST_RANGE_PRESETS[presetIdx];
        if ("fixed" in p && p.fixed) return p.fixed;
        return daysAgoIso(p.days);
      })();

  const togglePicked = (id: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const allPicked = (usersQ.data?.users || []).every((u) => picked.has(u.userId)) && (usersQ.data?.users.length || 0) > 0;
  const togglePickAll = () => {
    setPicked(() => {
      if (allPicked) return new Set();
      return new Set((usersQ.data?.users || []).map((u) => u.userId));
    });
  };

  const ingestM = useMutation({
    mutationFn: async (payload: { userIds: string[]; sinceDate: string }) => {
      return apiRequest("POST", "/api/admin/email-settings/ingest-historical", payload);
    },
    onSuccess: async (resp: any) => {
      const data = await (resp.json ? resp.json() : Promise.resolve(resp));
      toast({ title: "Ingest started", description: data?.message || "Historical mailbox scan kicked off." });
      setPicked(new Set());
      queryClient.invalidateQueries({ queryKey: ["/api/admin/email-settings/ingest-historical/status"] });
    },
    onError: (err: any) => {
      toast({ title: "Could not start ingest", description: err?.message || String(err), variant: "destructive" });
    },
  });

  const onIngest = () => {
    const userIds = Array.from(picked);
    if (userIds.length === 0) {
      toast({ title: "Pick at least one user", variant: "destructive" });
      return;
    }
    ingestM.mutate({ userIds, sinceDate });
  };

  const userById = new Map<string, IngestUser>();
  for (const u of usersQ.data?.users || []) userById.set(u.userId, u);

  const fmtDateTime = (s: string | null) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleString(); } catch { return s; }
  };
  const statusBadge = (status: string) => {
    if (status === "completed") return <Badge variant="outline" className="text-emerald-700 dark:text-emerald-300 border-emerald-500/40">Completed</Badge>;
    if (status === "completed_with_errors") return <Badge variant="outline" className="text-amber-700 dark:text-amber-300 border-amber-500/40">Partial</Badge>;
    if (status === "running") return <Badge variant="outline" className="text-blue-700 dark:text-blue-300 border-blue-500/40">Running</Badge>;
    if (status === "failed") return <Badge variant="destructive">Failed</Badge>;
    return <Badge variant="outline">{status}</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><Inbox className="h-5 w-5" /> Ingest historical email</h2>
          <p className="text-xs text-muted-foreground mt-1">Pull past Gmail conversations into the activity timeline. Matched emails (sent or received with leads/clients) get logged on their lead's timeline. Already-processed messages are deduped automatically.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => { usersQ.refetch(); statusQ.refetch(); }} disabled={usersQ.isRefetching || statusQ.isRefetching} data-testid="button-refresh-ingest">
          {(usersQ.isRefetching || statusQ.isRefetching) ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Refresh
        </Button>
      </div>

      <Card data-testid="card-ingest-form">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Start a new ingest</CardTitle>
          <CardDescription className="text-xs">Select one or more users with Gmail connected, choose how far back to scan, then start. Each user is processed independently in the background.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm">Users with Gmail connected</Label>
              {(usersQ.data?.users.length || 0) > 0 && (
                <Button type="button" variant="ghost" size="sm" onClick={togglePickAll} data-testid="button-toggle-all-ingest-users">
                  {allPicked ? "Clear all" : "Select all"}
                </Button>
              )}
            </div>
            {usersQ.isLoading ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading connected users…</div>
            ) : (usersQ.data?.users.length || 0) === 0 ? (
              <Alert>
                <AlertTitle className="text-sm flex items-center gap-2"><Info className="h-4 w-4" /> No Gmail-connected users</AlertTitle>
                <AlertDescription className="text-xs">Have your team connect their Gmail accounts under Settings → Integrations first, then come back here.</AlertDescription>
              </Alert>
            ) : (
              <ScrollArea className="border rounded-md max-h-56">
                <div className="p-2 space-y-1">
                  {(usersQ.data?.users || []).map((u) => (
                    <label key={u.userId} className="flex items-center gap-2 px-2 py-1.5 rounded hover-elevate cursor-pointer" data-testid={`row-ingest-user-${u.userId}`}>
                      <Checkbox checked={picked.has(u.userId)} onCheckedChange={() => togglePicked(u.userId)} data-testid={`checkbox-ingest-user-${u.userId}`} />
                      <span className="text-sm">{formatIngestUserLabel(u)}</span>
                    </label>
                  ))}
                </div>
              </ScrollArea>
            )}
            <p className="text-xs text-muted-foreground mt-1">{picked.size} selected</p>
          </div>

          <Separator />

          <div className="space-y-3">
            <Label className="text-sm">How far back?</Label>
            <div className="flex flex-wrap gap-2">
              {INGEST_RANGE_PRESETS.map((p, idx) => (
                <Button
                  key={p.label}
                  type="button"
                  size="sm"
                  variant={!usingCustom && presetIdx === idx ? "default" : "outline"}
                  onClick={() => { setPresetIdx(idx); setCustomDate(""); }}
                  data-testid={`button-ingest-preset-${idx}`}
                >
                  {p.label}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="ingest-custom-date" className="text-xs text-muted-foreground">Or pick a custom start date:</Label>
              <Input
                id="ingest-custom-date"
                type="date"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
                min="2024-01-01"
                className="w-44"
                data-testid="input-ingest-custom-date"
              />
            </div>
            <p className="text-xs text-muted-foreground">Effective start: <span className="font-mono">{sinceDate}</span></p>
          </div>

          <Separator />

          <div className="flex items-center justify-end">
            <Button onClick={onIngest} disabled={ingestM.isPending || picked.size === 0} data-testid="button-start-ingest">
              {ingestM.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
              Start ingest ({picked.size})
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="card-ingest-status">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Recent ingest jobs</CardTitle>
          <CardDescription className="text-xs">Auto-refreshes every 5 seconds. Includes admin-triggered ingests, the nightly 7-day sweep, and per-recipient backfills fired when leads/clients are created.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {statusQ.isLoading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground p-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading status…</div>
          ) : (statusQ.data?.rows.length || 0) === 0 ? (
            <p className="text-xs text-muted-foreground p-4">No ingest jobs have run yet.</p>
          ) : (
            <ScrollArea className="max-h-72">
              <table className="w-full text-xs" data-testid="table-ingest-status">
                <thead className="bg-muted/40 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">User</th>
                    <th className="text-left px-3 py-2 font-medium">Mode</th>
                    <th className="text-left px-3 py-2 font-medium">Status</th>
                    <th className="text-left px-3 py-2 font-medium">Since</th>
                    <th className="text-right px-3 py-2 font-medium">Processed</th>
                    <th className="text-right px-3 py-2 font-medium">Matched</th>
                    <th className="text-left px-3 py-2 font-medium">Started</th>
                    <th className="text-left px-3 py-2 font-medium">Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {(statusQ.data?.rows || []).map((r) => {
                    const u = userById.get(r.userId);
                    const userLabel = u ? (u.email || u.userId) : r.userId;
                    return (
                      <tr key={r.id} className="border-t" data-testid={`row-ingest-status-${r.id}`}>
                        <td className="px-3 py-2 align-top">
                          <div className="font-medium">{userLabel}</div>
                          {r.recipientEmail && <div className="text-muted-foreground text-[11px]">recipient: {r.recipientEmail}</div>}
                          {r.lastError && <div className="text-destructive text-[11px] mt-1">{r.lastError}</div>}
                        </td>
                        <td className="px-3 py-2 align-top capitalize">{r.mode}</td>
                        <td className="px-3 py-2 align-top">{statusBadge(r.status)}</td>
                        <td className="px-3 py-2 align-top font-mono text-[11px]">{r.sinceDate ? new Date(r.sinceDate).toISOString().slice(0, 10) : "—"}</td>
                        <td className="px-3 py-2 align-top text-right">{(r.processedCount ?? 0).toLocaleString()}</td>
                        <td className="px-3 py-2 align-top text-right">{(r.matchedCount ?? 0).toLocaleString()}</td>
                        <td className="px-3 py-2 align-top text-[11px]">{fmtDateTime(r.startedAt)}</td>
                        <td className="px-3 py-2 align-top text-[11px]">{fmtDateTime(r.completedAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── ESP Relay tab (Task #573 — bulk-email relay) ─────────────────────
// Configure a per-workspace ESP relay (Postmark only in this build) so
// bulk + drip mail can flow through warmed infrastructure while
// transactional sends stay on Gmail. The form is intentionally minimal —
// provider, API key, From identity, routing mode, enabled. Webhook URL
// is derived server-side and shown verbatim for the operator to paste
// into the ESP dashboard.
interface RelayConfigPublic {
  id?: string;
  provider: string;
  apiKeyLast4: string | null;
  hasApiKey: boolean;
  fromEmail: string;
  fromName: string | null;
  replyToEmail: string | null;
  sendingDomain: string | null;
  routingMode: "off" | "bulk_only";
  enabled: boolean;
  webhookToken: string;
  webhookUrl?: string;
  connectionVerifiedAt: string | null;
  lastSendAt: string | null;
  lastError: string | null;
  sendCount: number;
  bounceCount: number;
  spamComplaintCount: number;
  isProviderImplemented: boolean;
}

interface RelayProviderInfo { name: string; implemented: boolean }
interface RelayConfigResponse { config: RelayConfigPublic | null; providers: RelayProviderInfo[] }

function RelayTab() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<RelayConfigResponse>({ queryKey: ["/api/admin/email-relay"] });
  const config = data?.config ?? null;
  const providers = data?.providers ?? [];

  // Form state — initialised from server config. Editable inline.
  const [provider, setProvider] = useState<string>("postmark");
  const [apiKey, setApiKey] = useState<string>("");
  const [fromEmail, setFromEmail] = useState<string>("");
  const [fromName, setFromName] = useState<string>("");
  const [replyToEmail, setReplyToEmail] = useState<string>("");
  const [sendingDomain, setSendingDomain] = useState<string>("");
  const [routingMode, setRoutingMode] = useState<"off" | "bulk_only">("off");
  const [enabled, setEnabled] = useState<boolean>(false);
  const [verifyResult, setVerifyResult] = useState<{ ok: boolean; message: string } | null>(null);

  useEffect(() => {
    if (!config) return;
    setProvider(config.provider);
    setApiKey("");
    setFromEmail(config.fromEmail);
    setFromName(config.fromName ?? "");
    setReplyToEmail(config.replyToEmail ?? "");
    setSendingDomain(config.sendingDomain ?? "");
    setRoutingMode(config.routingMode);
    setEnabled(config.enabled);
  }, [config?.id, config?.provider, config?.fromEmail, config?.fromName, config?.replyToEmail,
      config?.sendingDomain, config?.routingMode, config?.enabled]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("PUT", "/api/admin/email-relay", {
        provider,
        apiKey: apiKey || undefined,
        fromEmail,
        fromName: fromName || null,
        replyToEmail: replyToEmail || null,
        sendingDomain: sendingDomain || null,
        routingMode,
        enabled,
      });
    },
    onSuccess: async () => {
      setApiKey("");
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/email-relay"] });
      toast({ title: "Relay settings saved" });
    },
    onError: (err: any) => {
      toast({ title: "Save failed", description: err?.message || "Unknown error", variant: "destructive" });
    },
  });

  const verifyMutation = useMutation({
    mutationFn: async (): Promise<{ ok: boolean; accountLabel?: string | null; verifiedDomains?: string[]; error?: string }> => {
      const res = await apiRequest("POST", "/api/admin/email-relay/verify", {
        provider,
        apiKey: apiKey || undefined,
      });
      return await res.json();
    },
    onSuccess: (result) => {
      if (result.ok) {
        const detail = [
          result.accountLabel ? `Account: ${result.accountLabel}` : null,
          result.verifiedDomains?.length ? `Verified: ${result.verifiedDomains.join(", ")}` : null,
        ].filter(Boolean).join(" • ");
        setVerifyResult({ ok: true, message: detail || "Connection successful." });
        toast({ title: "Relay connection OK" });
        queryClient.invalidateQueries({ queryKey: ["/api/admin/email-relay"] });
      } else {
        setVerifyResult({ ok: false, message: result.error || "Connection failed" });
        toast({ title: "Verify failed", description: result.error || "Unknown error", variant: "destructive" });
      }
    },
    onError: (err: any) => {
      setVerifyResult({ ok: false, message: err?.message || "Verify failed" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => apiRequest("DELETE", "/api/admin/email-relay"),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/email-relay"] });
      toast({ title: "Relay disconnected" });
    },
    onError: (err: any) => {
      toast({ title: "Delete failed", description: err?.message || "Unknown error", variant: "destructive" });
    },
  });

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(
      () => toast({ title: `${label} copied` }),
      () => toast({ title: "Copy failed", variant: "destructive" }),
    );
  };

  const selectedProviderInfo = providers.find((p) => p.name === provider);
  const canEnable = !!selectedProviderInfo?.implemented;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="status-relay-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" /> ESP Relay (bulk email)
          </CardTitle>
          <CardDescription>
            Route bulk + drip campaigns through a warmed Email Service Provider
            using your own API key. Transactional mail (invoices, payment links,
            scheduling) always stays on Gmail so replies flow back to the
            sender's inbox.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {config && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm" data-testid="status-relay-counters">
              <div className="rounded-md border p-3">
                <div className="text-muted-foreground text-xs">Sends</div>
                <div className="font-semibold" data-testid="text-relay-sends">{config.sendCount}</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-muted-foreground text-xs">Bounces</div>
                <div className="font-semibold" data-testid="text-relay-bounces">{config.bounceCount}</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-muted-foreground text-xs">Spam complaints</div>
                <div className="font-semibold" data-testid="text-relay-spam">{config.spamComplaintCount}</div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-muted-foreground text-xs">Last send</div>
                <div className="font-semibold text-xs">
                  {config.lastSendAt ? new Date(config.lastSendAt).toLocaleString() : "—"}
                </div>
              </div>
            </div>
          )}

          {config?.lastError && (
            <Alert variant="destructive" data-testid="alert-relay-last-error">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Last error</AlertTitle>
              <AlertDescription className="break-all">{config.lastError}</AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="relay-provider">Provider</Label>
              <Select value={provider} onValueChange={setProvider}>
                <SelectTrigger id="relay-provider" data-testid="select-relay-provider">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {providers.map((p) => (
                    <SelectItem key={p.name} value={p.name} data-testid={`option-relay-provider-${p.name}`}>
                      {p.name === "postmark" && "Postmark"}
                      {p.name === "resend" && "Resend"}
                      {p.name === "sendgrid" && "SendGrid"}
                      {p.name === "ses" && "Amazon SES"}
                      {!p.implemented && " (coming soon)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!canEnable && (
                <p className="text-xs text-muted-foreground mt-1">
                  This provider isn't wired up in this build yet — pick Postmark to enable routing.
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="relay-api-key">
                API Key {config?.hasApiKey && config.apiKeyLast4 ? (
                  <span className="text-xs text-muted-foreground font-normal">
                    (saved, ending in {config.apiKeyLast4} — leave blank to keep)
                  </span>
                ) : null}
              </Label>
              <Input
                id="relay-api-key"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={config?.hasApiKey ? "•••••••• " + (config.apiKeyLast4 || "") : "POSTMARK_SERVER_TOKEN"}
                autoComplete="off"
                data-testid="input-relay-api-key"
              />
            </div>

            <div>
              <Label htmlFor="relay-from-email">From email</Label>
              <Input
                id="relay-from-email"
                type="email"
                value={fromEmail}
                onChange={(e) => setFromEmail(e.target.value)}
                placeholder="bookings@send.example.com"
                data-testid="input-relay-from-email"
              />
            </div>

            <div>
              <Label htmlFor="relay-from-name">From name (optional)</Label>
              <Input
                id="relay-from-name"
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                placeholder="Warehouse Productions"
                data-testid="input-relay-from-name"
              />
            </div>

            <div>
              <Label htmlFor="relay-reply-to">Workspace Reply-To fallback (optional)</Label>
              <Input
                id="relay-reply-to"
                type="email"
                value={replyToEmail}
                onChange={(e) => setReplyToEmail(e.target.value)}
                placeholder="bookings@example.com"
                data-testid="input-relay-reply-to"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Bulk replies default to the <strong>sending user&rsquo;s</strong>
                {" "}mailbox so replies thread back into the rep&rsquo;s inbox.
                This field is only used as a fallback when the sending user
                has no email on file.
              </p>
            </div>

            <div>
              <Label htmlFor="relay-sending-domain">Sending domain (optional)</Label>
              <Input
                id="relay-sending-domain"
                value={sendingDomain}
                onChange={(e) => setSendingDomain(e.target.value)}
                placeholder="send.example.com"
                data-testid="input-relay-sending-domain"
              />
            </div>

            <div>
              <Label htmlFor="relay-routing-mode">Routing mode</Label>
              <Select value={routingMode} onValueChange={(v) => setRoutingMode(v === "bulk_only" ? "bulk_only" : "off")}>
                <SelectTrigger id="relay-routing-mode" data-testid="select-relay-routing-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="off" data-testid="option-relay-routing-off">Off — keep using Gmail for everything</SelectItem>
                  <SelectItem value="bulk_only" data-testid="option-relay-routing-bulk-only">Bulk only — relay campaigns + drip; keep transactional on Gmail</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end gap-3">
              <div className="flex items-center gap-2">
                <Switch
                  id="relay-enabled"
                  checked={enabled}
                  onCheckedChange={setEnabled}
                  disabled={!canEnable}
                  data-testid="switch-relay-enabled"
                />
                <Label htmlFor="relay-enabled" className="cursor-pointer">Enabled</Label>
              </div>
              {config?.connectionVerifiedAt && (
                <Badge variant="outline" className="gap-1" data-testid="badge-relay-verified">
                  <CheckCircle2 className="h-3 w-3 text-green-600" /> Verified
                </Badge>
              )}
            </div>
          </div>

          {config?.webhookUrl && (
            <div className="rounded-md border bg-muted/30 p-3 space-y-2" data-testid="status-relay-webhook">
              <Label className="text-xs">Webhook URL — paste this into your ESP dashboard</Label>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={config.webhookUrl}
                  className="font-mono text-xs"
                  data-testid="text-relay-webhook-url"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(config.webhookUrl!, "Webhook URL")}
                  data-testid="button-copy-webhook"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Configure your ESP to POST Bounce, SpamComplaint and SubscriptionChange
                events to this URL. Hard bounces and complaints automatically suppress
                the lead in this CRM.
              </p>
            </div>
          )}

          {verifyResult && (
            <Alert variant={verifyResult.ok ? "default" : "destructive"} data-testid="status-relay-verify-result">
              {verifyResult.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              <AlertTitle>{verifyResult.ok ? "Connection verified" : "Verification failed"}</AlertTitle>
              <AlertDescription>{verifyResult.message}</AlertDescription>
            </Alert>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              data-testid="button-save-relay"
            >
              {saveMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save settings
            </Button>
            <Button
              variant="outline"
              onClick={() => verifyMutation.mutate()}
              disabled={verifyMutation.isPending || (!apiKey && !config?.hasApiKey)}
              data-testid="button-verify-relay"
            >
              {verifyMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Test connection
            </Button>
            {config && (
              <Button
                variant="destructive"
                onClick={() => {
                  if (confirm("Disconnect the ESP relay? Bulk + drip will revert to Gmail on the next scheduler tick.")) {
                    deleteMutation.mutate();
                  }
                }}
                disabled={deleteMutation.isPending}
                data-testid="button-disconnect-relay"
              >
                {deleteMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Disconnect
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// =====================================================================
// Workspace Fallback Connection Card (Task #452)
// =====================================================================
interface IntegrationsSettingsResponse {
  emailFallbackEnabled: boolean;
  fallbackEmailUserId: string | null;
  fallbackEmailUserEmail: string | null;
  [key: string]: unknown;
}

function WorkspaceFallbackCard() {
  const { toast } = useToast();
  const { workspaceSlug } = useWorkspace();

  const { data: integrationsData, isLoading: integrationsLoading } = useQuery<IntegrationsSettingsResponse>({
    queryKey: ["/api/w", workspaceSlug, "settings/integrations"],
    enabled: !!workspaceSlug,
  });

  const { data: usersData } = useQuery<{ users: IngestUser[] }>({
    queryKey: ["/api/admin/email-settings/gmail-connected-users"],
  });

  const [fallbackEnabled, setFallbackEnabled] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (integrationsData && !initialized) {
      setFallbackEnabled(integrationsData.emailFallbackEnabled ?? false);
      setSelectedUserId(integrationsData.fallbackEmailUserId ?? null);
      setInitialized(true);
    }
  }, [integrationsData, initialized]);

  const toggleMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const res = await apiRequest("PATCH", `/api/w/${workspaceSlug}/settings/email-fallback`, { enabled });
      return res.json();
    },
    onSuccess: (_data: any, enabled: boolean) => {
      setFallbackEnabled(enabled);
      queryClient.invalidateQueries({ queryKey: ["/api/w", workspaceSlug, "settings/integrations"] });
      toast({ title: enabled ? "Fallback sender enabled" : "Fallback sender disabled" });
    },
    onError: (err: any) => toast({ title: "Failed to update", description: err?.message || String(err), variant: "destructive" }),
  });

  const senderMutation = useMutation({
    mutationFn: async (userId: string | null) => {
      const res = await apiRequest("PATCH", `/api/w/${workspaceSlug}/settings/email-fallback-sender`, { userId });
      return res.json();
    },
    onSuccess: (_data: any, userId: string | null) => {
      setSelectedUserId(userId);
      queryClient.invalidateQueries({ queryKey: ["/api/w", workspaceSlug, "settings/integrations"] });
      toast({ title: userId ? "Fallback sender saved" : "Fallback sender cleared" });
    },
    onError: (err: any) => toast({ title: "Failed to update sender", description: err?.message || String(err), variant: "destructive" }),
  });

  const users = usersData?.users ?? [];
  const selectedUser = users.find(u => u.userId === selectedUserId);
  const isBusy = toggleMutation.isPending || senderMutation.isPending;

  if (integrationsLoading) {
    return (
      <Card data-testid="card-workspace-fallback">
        <CardContent className="py-6 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="card-workspace-fallback">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Mail className="h-4 w-4" /> Workspace Fallback Sender
          <HelpTip text="If an automated send fails (disconnected mailbox, billing issue, etc.), retries can go out through this backup Gmail connection instead." />
        </CardTitle>
        <CardDescription className="text-xs">
          Failed sends are always queued in the outbox for automatic retry. A fallback sender lets those retries go out via a backup Gmail connection if the original sender is disconnected.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm">Enable fallback sender</Label>
            <p className="text-xs text-muted-foreground">When on, outbox retries use the selected member's Gmail if the original sender is unavailable.</p>
          </div>
          <Switch
            checked={fallbackEnabled}
            onCheckedChange={(v) => toggleMutation.mutate(v)}
            disabled={isBusy}
            data-testid="switch-fallback-enabled"
          />
        </div>

        {fallbackEnabled && (
          <>
            <Separator />
            <div className="space-y-2">
              <Label className="text-sm">Fallback sender</Label>
              {users.length === 0 ? (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle className="text-sm">No connected Gmail accounts</AlertTitle>
                  <AlertDescription className="text-xs">Ask workspace members to connect their Gmail under Integrations to appear here.</AlertDescription>
                </Alert>
              ) : (
                <Select
                  value={selectedUserId ?? ""}
                  onValueChange={(v) => setSelectedUserId(v || null)}
                  disabled={isBusy}
                >
                  <SelectTrigger data-testid="select-fallback-sender">
                    <SelectValue placeholder="Select a workspace member…" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((u) => (
                      <SelectItem key={u.userId} value={u.userId} data-testid={`option-fallback-sender-${u.userId}`}>
                        {formatIngestUserLabel(u)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {selectedUserId && selectedUser && (
                <p className="text-xs text-muted-foreground" data-testid="text-fallback-sender-email">
                  Sends will fall back to <span className="font-medium">{selectedUser.email || "(no email)"}</span>
                </p>
              )}
              {selectedUserId && !selectedUser && integrationsData?.fallbackEmailUserEmail && (
                <p className="text-xs text-muted-foreground" data-testid="text-fallback-sender-email">
                  Saved sender: <span className="font-medium">{integrationsData.fallbackEmailUserEmail}</span>
                </p>
              )}

              <div className="flex items-center gap-2 pt-1">
                <Button
                  size="sm"
                  onClick={() => senderMutation.mutate(selectedUserId)}
                  disabled={isBusy || !selectedUserId}
                  data-testid="button-save-fallback-sender"
                >
                  {senderMutation.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setSelectedUserId(null);
                    senderMutation.mutate(null);
                  }}
                  disabled={isBusy || !integrationsData?.fallbackEmailUserId}
                  data-testid="button-clear-fallback-sender"
                >
                  Clear
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// =====================================================================
// Deliverability checklist — surfaces incomplete compliance items at the
// top of the Dashboard tab. Reads from the same overview query so there
// is no extra network request. Hides entirely when all items pass.
// =====================================================================
interface DeliverabilityChecklistProps {
  onNavigate: (outerTab: string, innerSubTab?: string) => void;
}

function DeliverabilityChecklist({ onNavigate }: DeliverabilityChecklistProps) {
  const { data, isLoading } = useQuery<OverviewResponse>({
    queryKey: ["/api/admin/email-settings/overview"],
  });

  if (isLoading || !data) return null;

  const items: Array<{
    key: string;
    icon: React.ReactNode;
    label: string;
    description: string;
    actionLabel: string;
    onAction: () => void;
  }> = [];

  if (data.postmaster.configured && !data.postmaster.connected) {
    items.push({
      key: "postmaster",
      icon: <ShieldCheck className="h-4 w-4 text-amber-600" />,
      label: "Postmaster Tools not connected",
      description: "Connect Google Postmaster to monitor domain reputation and spam rates in Gmail.",
      actionLabel: "Connect",
      onAction: () => onNavigate("warm", "postmaster"),
    });
  }

  const hasNoVerifiedDomain = data.domains.total === 0 || data.domains.healthy === 0;
  if (hasNoVerifiedDomain) {
    items.push({
      key: "domains",
      icon: <Globe className="h-4 w-4 text-amber-600" />,
      label: "No verified sending domain",
      description: "Add and verify SPF, DKIM, and DMARC records for your sending domain.",
      actionLabel: "Add domain",
      onAction: () => onNavigate("warm", "domains"),
    });
  }

  if (items.length === 0) return null;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/20 p-4 space-y-3" data-testid="section-deliverability-checklist">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
        <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200">Setup incomplete</h3>
        <span className="ml-auto text-xs text-amber-700 dark:text-amber-400">{items.length} item{items.length === 1 ? "" : "s"} remaining</span>
      </div>
      <div className="divide-y divide-amber-200/60 dark:divide-amber-900/40">
        {items.map((item) => (
          <div key={item.key} className="flex items-start gap-3 py-2.5 first:pt-0 last:pb-0" data-testid={`checklist-item-${item.key}`}>
            <div className="mt-0.5 shrink-0">{item.icon}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200">{item.label}</p>
              <p className="text-xs text-amber-700/80 dark:text-amber-400/80">{item.description}</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 text-xs border-amber-300 text-amber-900 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-200 dark:hover:bg-amber-900/40"
              onClick={item.onAction}
              data-testid={`button-checklist-${item.key}`}
            >
              {item.actionLabel}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// =====================================================================
// Warm Email area — connected-mailbox reputation, auth, identity, ingest
// =====================================================================
interface WarmEmailAreaProps {
  activeSubTab?: string;
  onSubTabChange?: (sub: string) => void;
}

function WarmEmailArea({ activeSubTab, onSubTabChange }: WarmEmailAreaProps) {
  const [localSubTab, setLocalSubTab] = useState("reputation");
  const subTab = activeSubTab ?? localSubTab;
  const setSubTab = (v: string) => {
    setLocalSubTab(v);
    onSubTabChange?.(v);
  };
  return (
    <Tabs value={subTab} onValueChange={setSubTab} className="w-full">
      <TabsList className="mb-3 flex flex-wrap h-auto">
        <TabsTrigger value="reputation" data-testid="subtab-reputation">Reputation</TabsTrigger>
        <TabsTrigger value="domains" data-testid="subtab-domains">Domains</TabsTrigger>
        <TabsTrigger value="postmaster" data-testid="subtab-postmaster">Google Postmaster</TabsTrigger>
        <TabsTrigger value="history" data-testid="subtab-history">Historical Ingest</TabsTrigger>
      </TabsList>
      <TabsContent value="reputation" className="space-y-6">
        <SenderReputationTab />
        <Separator />
        <DomainHealthScorecardTab />
      </TabsContent>
      <TabsContent value="domains"><DomainAuthTab /></TabsContent>
      <TabsContent value="postmaster"><PostmasterSection /></TabsContent>
      <TabsContent value="history"><IngestTab /></TabsContent>
    </Tabs>
  );
}

// =====================================================================
// Cold-reply forwarding target (Task #406). When set, an inbound reply on a
// MANAGED (lease/buy) cold mailbox is forwarded as a copy to this human inbox
// (in addition to landing in the unified inbox). Does NOT apply to BYOD
// mailboxes, and the warm Gmail path is unaffected.
// =====================================================================
interface ColdReplyForwardResponse {
  forwardEmail: string | null;
  suggestedEmail: string | null;
  hasManagedColdMailbox: boolean;
  hasRelayConfig: boolean;
}
function ColdReplyForwardCard() {
  const { toast } = useToast();
  const { data, isLoading } = useQuery<ColdReplyForwardResponse>({
    queryKey: ["/api/admin/cold-reply-forward"],
  });
  const [value, setValue] = useState<string>("");
  const [dirty, setDirty] = useState(false);

  // Prefill with the stored value, falling back to the suggested admin email
  // so the target isn't a silent null. The user edits from there.
  useEffect(() => {
    if (data && !dirty) {
      setValue(data.forwardEmail ?? data.suggestedEmail ?? "");
    }
  }, [data, dirty]);

  const saveMutation = useMutation({
    mutationFn: (forwardEmail: string) =>
      apiRequest("PUT", "/api/admin/cold-reply-forward", { forwardEmail }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/cold-reply-forward"] });
      setDirty(false);
      toast({ title: "Forward target saved" });
    },
    onError: async (err: any) => {
      let message = "Failed to save forward target";
      try {
        const body = await err?.response?.json?.();
        if (body?.message) message = body.message;
      } catch { /* ignore */ }
      toast({ title: message, variant: "destructive" });
    },
  });

  return (
    <Card data-testid="card-cold-reply-forward">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Inbox className="h-4 w-4" /> Cold-reply forwarding
          <HelpTip text="When a prospect replies to a cold outreach email on a managed (Lease/Buy) mailbox, we forward a copy to this inbox so a person sees it right away. The reply also still lands in your unified inbox." />
        </CardTitle>
        <CardDescription className="text-xs">
          Replies on managed (Lease/Buy) cold mailboxes are forwarded here, in addition to landing in your unified inbox. This does not apply to bring-your-own mailboxes.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {data && !data.hasManagedColdMailbox && (
              <Alert data-testid="alert-no-managed-cold">
                <Info className="h-4 w-4" />
                <AlertTitle className="text-sm">No managed cold sending yet</AlertTitle>
                <AlertDescription className="text-xs">
                  Set up managed cold sending (Lease or Buy) above before configuring a forward target.
                </AlertDescription>
              </Alert>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="cold-forward-email" className="text-xs">Forward replies to</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="cold-forward-email"
                  type="email"
                  placeholder="you@yourcompany.com"
                  value={value}
                  disabled={!data?.hasManagedColdMailbox}
                  onChange={(e) => { setValue(e.target.value); setDirty(true); }}
                  data-testid="input-cold-forward-email"
                />
                <Button
                  size="sm"
                  onClick={() => saveMutation.mutate(value.trim())}
                  disabled={!data?.hasManagedColdMailbox || saveMutation.isPending || !dirty}
                  data-testid="button-save-cold-forward"
                >
                  {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Leave blank and save to turn forwarding off. Per-campaign control lives on each cold drip campaign.
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// =====================================================================
// Cold Email area — redesigned 3-tab structure.
//   · Empty state  → only shows buy/lease packages
//   · Has setup    → Dashboard | Set-Up | Buy/Lease tabs
// =====================================================================

interface ColdInfraPurchaseLite {
  id: string;
  provisioningStatus: string;
}

interface ColdSendingDomain {
  id: string;
  domain: string;
  purpose: "primary" | "cold";
  spfVerified: boolean;
  dkimVerified: boolean;
  returnPathVerified: boolean;
}

interface ColdMailboxRow {
  mailboxId: string;
  email: string;
  reputationScore: number | null;
  warmupStartDate: string | null;
  lastIncidentAt: string | null;
  isPaused?: boolean;
  pausedReason?: string | null;
  warmupTargetPerDay: number | null;
}

function coldMbTier(mb: ColdMailboxRow): { label: string; cls: string; days: number } {
  const start = mb.warmupStartDate ? new Date(mb.warmupStartDate) : null;
  if (!start || !Number.isFinite(start.getTime())) {
    return { label: "New", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400", days: 0 };
  }
  const incident = mb.lastIncidentAt ? new Date(mb.lastIncidentAt) : null;
  const cleanSince = incident && incident.getTime() > start.getTime() ? incident : start;
  const days = Math.max(0, Math.floor((Date.now() - cleanSince.getTime()) / 86_400_000));
  if (days >= 90) return { label: "Warmed", cls: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400", days };
  if (days >= 60) return { label: "Established", cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400", days };
  if (days >= 30) return { label: "Warming", cls: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400", days };
  return { label: "New", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400", days };
}

function ColdKpiCard({
  icon: Icon,
  label,
  value,
  sub,
  accentCls = "text-primary",
}: {
  icon: (props: { className?: string }) => JSX.Element;
  label: string;
  value: string | number;
  sub: string;
  accentCls?: string;
}) {
  return (
    <div className="rounded-xl border bg-card px-4 py-3.5 flex items-start gap-3">
      <div className="rounded-lg bg-muted p-2 shrink-0">
        <Icon className={cn("h-4 w-4", accentCls)} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest truncate">{label}</p>
        <p className="text-2xl font-bold leading-tight mt-0.5 tabular-nums">{value}</p>
        <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{sub}</p>
      </div>
    </div>
  );
}

function ColdDashboardPanel() {
  const domainsQ = useQuery<ColdSendingDomain[] | { domains?: ColdSendingDomain[] }>({
    queryKey: ["/api/admin/sending-domains"],
    staleTime: 60_000,
  });
  const mailboxesQ = useQuery<ColdMailboxRow[]>({
    queryKey: ["/api/admin/deliverability/all-mailboxes"],
    staleTime: 60_000,
  });
  const trendingQ = useQuery<{
    totals: { sent: number; bounces: number; replies: number; optOuts: number };
  }>({
    queryKey: ["/api/admin/deliverability/trending", { days: 7 }],
    staleTime: 300_000,
  });

  const coldDomains = useMemo<ColdSendingDomain[]>(() => {
    const raw = domainsQ.data;
    const all: ColdSendingDomain[] = Array.isArray(raw) ? raw : (raw as any)?.domains ?? [];
    return all.filter((d) => d.purpose === "cold");
  }, [domainsQ.data]);

  const mailboxes = useMemo(() => mailboxesQ.data ?? [], [mailboxesQ.data]);

  const mbByDomain = useMemo(() => {
    const map = new Map<string, ColdMailboxRow[]>();
    for (const mb of mailboxes) {
      const dom = mb.email.split("@")[1] ?? "";
      if (!map.has(dom)) map.set(dom, []);
      map.get(dom)!.push(mb);
    }
    return map;
  }, [mailboxes]);

  const trending = trendingQ.data;
  const sent7d = trending?.totals.sent ?? 0;
  const bounceRate = sent7d > 0 ? (trending?.totals.bounces ?? 0) / sent7d : 0;
  const activeDomains = coldDomains.filter((d) => d.spfVerified && d.dkimVerified).length;
  const activeMbs = mailboxes.filter((m) => !m.isPaused).length;
  const pausedCount = mailboxes.length - activeMbs;
  const avgRep = mailboxes.length
    ? Math.round(mailboxes.reduce((s, m) => s + (m.reputationScore ?? 50), 0) / mailboxes.length)
    : null;

  if (domainsQ.isLoading || mailboxesQ.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <ColdKpiCard
          icon={(p) => <Globe {...p} />}
          label="Active Domains"
          value={activeDomains}
          sub={coldDomains.length - activeDomains > 0 ? `${coldDomains.length - activeDomains} unverified` : "all verified"}
          accentCls="text-blue-500"
        />
        <ColdKpiCard
          icon={(p) => <Mail {...p} />}
          label="Active Mailboxes"
          value={activeMbs}
          sub={pausedCount > 0 ? `${pausedCount} paused` : "all healthy"}
          accentCls="text-indigo-500"
        />
        <ColdKpiCard
          icon={(p) => <Send {...p} />}
          label="Sent (7 days)"
          value={sent7d > 0 ? sent7d.toLocaleString() : "—"}
          sub={sent7d > 0 ? `${(bounceRate * 100).toFixed(1)}% bounce rate` : "no sends yet"}
          accentCls={bounceRate > 0.04 ? "text-red-500" : bounceRate > 0.02 ? "text-yellow-500" : "text-emerald-500"}
        />
        <ColdKpiCard
          icon={(p) => <ShieldCheck {...p} />}
          label="Avg Reputation"
          value={avgRep != null ? `${avgRep}%` : "—"}
          sub={avgRep != null ? (avgRep >= 70 ? "healthy" : avgRep >= 40 ? "needs attention" : "at risk") : "warming up"}
          accentCls={avgRep == null ? "text-muted-foreground" : avgRep >= 70 ? "text-green-500" : avgRep >= 40 ? "text-yellow-500" : "text-red-500"}
        />
      </div>

      {/* Domains grid */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Globe className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">Cold Sending Domains</h3>
          <Badge variant="outline" className="text-[10px] h-5 px-1.5">{coldDomains.length}</Badge>
        </div>
        {coldDomains.length === 0 ? (
          <div className="rounded-xl border border-dashed px-6 py-8 text-center text-sm text-muted-foreground">
            Domains will appear here once provisioning completes.
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {coldDomains.map((d) => {
              const dMbs = mbByDomain.get(d.domain) ?? [];
              const dAvgRep = dMbs.length
                ? Math.round(dMbs.reduce((s, m) => s + (m.reputationScore ?? 50), 0) / dMbs.length)
                : null;
              const verified = d.spfVerified && d.dkimVerified;
              const dPaused = dMbs.filter((m) => m.isPaused).length;
              return (
                <div key={d.id} className="rounded-xl border bg-card p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-mono text-sm font-semibold truncate min-w-0">{d.domain}</p>
                    <Badge
                      variant="outline"
                      className={cn(
                        "text-[10px] shrink-0",
                        verified
                          ? "border-green-300 text-green-700 dark:border-green-700 dark:text-green-400"
                          : "border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-400",
                      )}
                    >
                      {verified ? "✓ Verified" : "⚠ Partial"}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-3 divide-x text-center text-xs">
                    <div className="pr-2">
                      <p className="text-lg font-bold">{dMbs.length}</p>
                      <p className="text-muted-foreground">Mailboxes</p>
                    </div>
                    <div className="px-2">
                      <p className={cn("text-lg font-bold tabular-nums", dAvgRep == null ? "text-muted-foreground" : dAvgRep >= 70 ? "text-green-600" : dAvgRep >= 40 ? "text-yellow-600" : "text-red-600")}>
                        {dAvgRep != null ? `${dAvgRep}%` : "—"}
                      </p>
                      <p className="text-muted-foreground">Avg Rep</p>
                    </div>
                    <div className="pl-2">
                      <p className={cn("text-lg font-bold", dPaused > 0 ? "text-amber-600" : "text-green-600")}>
                        {dMbs.length - dPaused}
                      </p>
                      <p className="text-muted-foreground">Active</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] border-t pt-2.5">
                    <span className={cn("flex items-center gap-1", d.spfVerified ? "text-green-600" : "text-red-500")}>
                      {d.spfVerified ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />} SPF
                    </span>
                    <span className={cn("flex items-center gap-1", d.dkimVerified ? "text-green-600" : "text-red-500")}>
                      {d.dkimVerified ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />} DKIM
                    </span>
                    <span className={cn("flex items-center gap-1", d.returnPathVerified ? "text-green-600" : "text-muted-foreground")}>
                      {d.returnPathVerified ? <CheckCircle2 className="h-3 w-3" /> : <Info className="h-3 w-3" />} Return-Path
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Mailbox health table */}
      <section data-section="mailbox-health">
        <div className="flex items-center gap-2 mb-3">
          <Mail className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">Mailbox Health</h3>
          <Badge variant="outline" className="text-[10px] h-5 px-1.5">{mailboxes.length}</Badge>
        </div>
        {mailboxes.length === 0 ? (
          <div className="rounded-xl border border-dashed px-6 py-8 text-center text-sm text-muted-foreground">
            Mailboxes will appear here once provisioning completes.
          </div>
        ) : (
          <div className="rounded-xl border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted/40 text-left border-b">
                    <th className="px-3 py-2.5 font-semibold text-muted-foreground">Mailbox</th>
                    <th className="px-3 py-2.5 font-semibold text-muted-foreground hidden sm:table-cell">Tier</th>
                    <th className="px-3 py-2.5 font-semibold text-muted-foreground">Reputation</th>
                    <th className="px-3 py-2.5 font-semibold text-muted-foreground hidden md:table-cell">Clean Days</th>
                    <th className="px-3 py-2.5 font-semibold text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {mailboxes.map((mb) => {
                    const tier = coldMbTier(mb);
                    const rep = mb.reputationScore;
                    return (
                      <tr key={mb.mailboxId} className="hover:bg-muted/20 transition-colors">
                        <td className="px-3 py-2.5 font-mono max-w-[180px] truncate">{mb.email}</td>
                        <td className="px-3 py-2.5 hidden sm:table-cell">
                          <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-semibold", tier.cls)}>
                            {tier.label}
                          </span>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-14 h-1.5 rounded-full bg-muted overflow-hidden hidden sm:block">
                              <div
                                className={cn("h-full rounded-full transition-all", rep != null ? (rep >= 70 ? "bg-green-500" : rep >= 40 ? "bg-yellow-500" : "bg-red-500") : "bg-muted-foreground/30")}
                                style={{ width: `${Math.min(100, rep ?? 0)}%` }}
                              />
                            </div>
                            <span className={cn("font-semibold tabular-nums", rep != null ? (rep >= 70 ? "text-green-600" : rep >= 40 ? "text-yellow-600" : "text-red-600") : "text-muted-foreground")}>
                              {rep != null ? `${rep}%` : "—"}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 hidden md:table-cell text-muted-foreground tabular-nums">{tier.days}d</td>
                        <td className="px-3 py-2.5">
                          {mb.isPaused ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                              Paused
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                              Active
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Email Health Overview */}
      <section>
        <OverviewTab />
      </section>
    </div>
  );
}

function ColdSetupTab() {
  return (
    <div className="space-y-8">
      <section>
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-4">Sender Identity</p>
        <ComplianceTab variant="identity" />
      </section>
      <Separator />
      <section>
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-4">CAN-SPAM Compliance</p>
        <ComplianceTab variant="canspam" />
      </section>
      <Separator />
      <section>
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-4">Audience Policy</p>
        <ColdEmailPolicyCard />
      </section>
      <Separator />
      <section>
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-4">Reply Forwarding</p>
        <ColdReplyForwardCard />
      </section>
    </div>
  );
}

function ColdByodDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const byod = COLD_MODE_FRAMING.byod;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full sm:max-w-2xl max-h-[85vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Server className="h-4 w-4" /> {byod.label}: connect your own infrastructure
          </DialogTitle>
          <DialogDescription>{byod.tagline}</DialogDescription>
        </DialogHeader>
        <Alert variant="destructive" data-testid="alert-byod-warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Managed deliverability is OFF for bring-your-own</AlertTitle>
          <AlertDescription className="text-xs">{byod.description}</AlertDescription>
        </Alert>
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Your own cold sending domains</h3>
          <p className="text-xs text-muted-foreground">
            Add a dedicated subdomain you own (e.g. <code>send.yourcompany.com</code>) and verify SPF/DKIM yourself.
          </p>
          <SendingDomainsTab showRequestCard={false} />
        </div>
        <Separator />
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Your own sending relay (ESP)</h3>
          <p className="text-xs text-muted-foreground">
            Route cold sends through an external email service provider you control.
          </p>
          <RelayTab />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ColdEmailArea() {
  const [byodOpen, setByodOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("byod") === "1";
  });

  const { data: purchasesData, isLoading: purchasesLoading } = useQuery<
    { purchases: ColdInfraPurchaseLite[] } | ColdInfraPurchaseLite[]
  >({
    queryKey: ["/api/admin/infra/purchases"],
    staleTime: 30_000,
  });

  const purchases: ColdInfraPurchaseLite[] = Array.isArray(purchasesData)
    ? purchasesData
    : (purchasesData as any)?.purchases ?? [];

  const hasActiveSetup = purchases.some(
    (p) => !["cancelled", "failed"].includes(p.provisioningStatus),
  );
  const hasInFlight = purchases.some((p) =>
    ["provisioning", "awaiting_capacity"].includes(p.provisioningStatus),
  );

  if (purchasesLoading) {
    return (
      <div className="flex justify-center py-14">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  /* ── Empty state: no active setup ── */
  if (!hasActiveSetup) {
    return (
      <div className="space-y-6" data-testid="cold-email-empty-state">
        <div className="text-center px-4 pt-8 pb-2">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-blue-100 dark:bg-blue-900/30 mb-4">
            <Snowflake className="h-7 w-7 text-blue-500" />
          </div>
          <h3 className="text-lg font-semibold">Start Cold Outreach</h3>
          <p className="text-sm text-muted-foreground mt-1.5 max-w-md mx-auto">
            Get dedicated domains and mailboxes with managed warmup, auto-verified SPF/DKIM, and full deliverability monitoring.
          </p>
        </div>
        <InfraStorefront surface="tenant" />
        <div className="rounded-lg border bg-muted/30 p-3 flex items-start gap-3" data-testid="card-byod-entry">
          <Server className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">Already have your own infrastructure?</p>
            <p className="text-xs text-muted-foreground">Connect your own domains and relay instead of using managed infrastructure.</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setByodOpen(true)} data-testid="button-open-byod">
            Bring your own
          </Button>
        </div>
        <ColdByodDialog open={byodOpen} onOpenChange={setByodOpen} />
      </div>
    );
  }

  /* ── Active setup: 3-tab layout ── */
  return (
    <div data-testid="cold-email-active">
      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList className="mb-5">
          <TabsTrigger value="dashboard" className="gap-1.5" data-testid="subtab-cold-dashboard">
            <LayoutDashboard className="h-3.5 w-3.5" /> Dashboard
          </TabsTrigger>
          <TabsTrigger value="setup" className="gap-1.5" data-testid="subtab-cold-setup">
            <Settings2 className="h-3.5 w-3.5" /> Set-Up
          </TabsTrigger>
          <TabsTrigger value="buy" className="gap-1.5" data-testid="subtab-cold-buy">
            <Plus className="h-3.5 w-3.5" /> Buy / Lease
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-0 space-y-4">
          {hasInFlight && (
            <Alert>
              <Loader2 className="h-4 w-4 animate-spin" />
              <AlertTitle>Provisioning in progress</AlertTitle>
              <AlertDescription>
                Your infrastructure is being set up. Domains and mailboxes will appear below once ready (typically a few minutes).
              </AlertDescription>
            </Alert>
          )}
          <ColdDashboardPanel />
        </TabsContent>

        <TabsContent value="setup" className="mt-0">
          <ColdSetupTab />
        </TabsContent>

        <TabsContent value="buy" className="mt-0 space-y-4">
          <InfraStorefront surface="tenant" />
          <div className="rounded-lg border bg-muted/30 p-3 flex items-start gap-3">
            <Server className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">Prefer to bring your own infrastructure?</p>
              <p className="text-xs text-muted-foreground">Connect your own domains and relay instead of using managed infrastructure.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setByodOpen(true)} data-testid="button-open-byod-2">
              Bring your own
            </Button>
          </div>
        </TabsContent>
      </Tabs>
      <ColdByodDialog open={byodOpen} onOpenChange={setByodOpen} />
    </div>
  );
}

function getTabParam(): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("tab");
}

// Three top-level areas: warm (Workspace Emails) / cold / alerts. The
// standalone Dashboard tab was removed (Task #572) — the Email Health Overview
// card moved into Cold Email and the Workspace Fallback Sender card into
// Workspace Emails. Map legacy deep-links onto the right area so existing URLs
// still land sensibly: health-related links open Cold Email, everything else
// defaults to Workspace Emails.
function mapTabParamToArea(t: string | null): string {
  if (t === "warm" || t === "cold" || t === "alerts") return t;
  if (t === "dashboard" || t === "overview") return "cold";
  if (t === "reputation" || t === "domains" || t === "postmaster" || t === "ingest") return "warm";
  if (t === "relay" || t === "coldEmailPolicy" || t === "identity") return "cold";
  if (t === "compliance") return "cold";
  return "warm";
}

export function EmailSettingsDialog({ open, onOpenChange, inPanel = false }: EmailSettingsDialogProps) {
  const { toast } = useToast();
  const search = useSearch();

  const [tab, setTab] = useState<string>(() => mapTabParamToArea(getTabParam()));

  // Keep the top-level area in sync with the URL's ?tab= param. wouter's
  // setLocation with a ?query doesn't remount this component, so a deep-link
  // (e.g. the Cold Email Health card's "Set up cold sending domain" button →
  // ?section=emailSettings&tab=cold) must reactively switch the tab here.
  useEffect(() => {
    const t = getTabParam();
    if (t) setTab(mapTabParamToArea(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  const [warmSubTab, setWarmSubTab] = useState<string>("reputation");

  // Post-Gmail-connect nudge: detect ?gmail=connected, check if Postmaster
  // is still disconnected, and prompt the admin to finish deliverability setup.
  const [showPostmasterNudge, setShowPostmasterNudge] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);

  const { data: overviewData } = useQuery<OverviewResponse>({
    queryKey: ["/api/admin/email-settings/overview"],
  });

  useEffect(() => {
    if (nudgeDismissed) return;
    const params = new URLSearchParams(window.location.search);
    const gmailParam = params.get("gmail");
    if (gmailParam === "connected" && overviewData) {
      const postmasterDisconnected =
        overviewData.postmaster.configured && !overviewData.postmaster.connected;
      if (postmasterDisconnected) {
        setShowPostmasterNudge(true);
      }
    }
  }, [overviewData, nudgeDismissed]);

  const connectPostmasterMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/email-settings/postmaster/connect", {});
      return res.json() as Promise<{ authUrl: string }>;
    },
    onSuccess: (r) => { window.location.href = r.authUrl; },
    onError: (err: any) => toast({ title: "Failed to start Postmaster connection", description: err?.message || String(err), variant: "destructive" }),
  });

  // Navigate to an outer tab and optionally a warm sub-tab
  const handleNavigate = (outerTab: string, innerSubTab?: string) => {
    setTab(outerTab);
    if (outerTab === "warm" && innerSubTab) {
      setWarmSubTab(innerSubTab);
    }
  };

  const inner = (
    <div className="w-full h-full flex flex-col overflow-auto p-4 sm:p-6 [scrollbar-gutter:stable]">
      {!inPanel && (
        <div className="mb-4">
          <h1 className="text-xl font-semibold flex items-center gap-2"><Mail className="h-5 w-5" /> Email</h1>
          <p className="text-sm text-muted-foreground">Your warm and cold email programs — health, reputation, compliance, and alerts in one place.</p>
        </div>
      )}

      {/* Post-Gmail-connect nudge — shown once per session after Gmail connects
          if Postmaster hasn't been set up yet. Dismissed by connecting or skipping. */}
      {showPostmasterNudge && !nudgeDismissed && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-900/50 dark:bg-blue-950/20 p-4 space-y-3" data-testid="card-postmaster-nudge">
          <div className="flex items-start gap-3">
            <ShieldCheck className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">Complete your deliverability setup</p>
              <p className="text-xs text-blue-700/80 dark:text-blue-400/80 mt-0.5">
                Gmail is connected — great start. Connect Google Postmaster Tools to monitor your domain's reputation and spam rates in Gmail so you catch issues before they affect delivery.
              </p>
            </div>
            <button
              type="button"
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 text-xs leading-none mt-0.5"
              onClick={() => { setShowPostmasterNudge(false); setNudgeDismissed(true); }}
              aria-label="Dismiss"
              data-testid="button-nudge-dismiss"
            >
              ✕
            </button>
          </div>
          <div className="flex items-center gap-3 pl-8">
            <Button
              size="sm"
              onClick={() => connectPostmasterMutation.mutate()}
              disabled={connectPostmasterMutation.isPending}
              data-testid="button-nudge-connect-postmaster"
            >
              {connectPostmasterMutation.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              <LinkIcon className="h-4 w-4 mr-1" />
              Connect Google Postmaster
            </Button>
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground underline"
              onClick={() => { setShowPostmasterNudge(false); setNudgeDismissed(true); }}
              data-testid="button-nudge-skip"
            >
              Skip for now
            </button>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col">
        <TabsList className="grid w-full grid-cols-3 mb-4 h-auto">
          <TabsTrigger value="warm" data-testid="tab-warm" className="gap-1.5"><Flame className="h-4 w-4" /> Workspace Emails</TabsTrigger>
          <TabsTrigger value="cold" data-testid="tab-cold" className="gap-1.5"><Snowflake className="h-4 w-4" /> Cold Email</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts" className="gap-1.5"><Bell className="h-4 w-4" /> Alerts</TabsTrigger>
        </TabsList>
        <TabsContent value="warm">
          <div className="space-y-6">
            <DeliverabilityChecklist onNavigate={handleNavigate} />
            <WorkspaceFallbackCard />
            <Separator />
            <WarmEmailArea activeSubTab={warmSubTab} onSubTabChange={setWarmSubTab} />
          </div>
        </TabsContent>
        <TabsContent value="cold">
          <div className="space-y-6">
            <ColdEmailArea />
          </div>
        </TabsContent>
        <TabsContent value="alerts"><AlertsTab /></TabsContent>
      </Tabs>
    </div>
  );

  if (inPanel) return inner;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full sm:max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {inner}
      </DialogContent>
    </Dialog>
  );
}

export default EmailSettingsDialog;
